import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton, useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Target, Trophy, Sparkles, Zap, ArrowRight, Users, BarChart3, Skull, Coins, Gamepad2, Timer, ChevronRight, TrendingUp, Crown, Wallet, Flame, Shield, Clock, BookOpen, Eye, Star, Unlock, Scroll, Compass, Activity, Library } from 'lucide-react';
import HomeFooter from '../components/HomeFooter';
import LiveModeAuthModal from '../components/LiveModeAuthModal';
import { useDemo, useAppMode } from '../context/DemoContext';
import { useLiveMode } from '../hooks/useLiveMode';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useChancellorHeartbeat } from '../hooks/useChancellorHeartbeat';
import DemoTransition from '../components/DemoTransition';
import '@fontsource/cinzel-decorative/400.css';
import '@fontsource/cinzel-decorative/700.css';

// Cinematic section assets (MidJourney, webp)
import lavaBg from '../assets/seasons/lava_bg.webp';
import shrineBg from '../assets/genesis/shrine_bg.webp';
import inscriptionSlab from '../assets/genesis/inscription_slab.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_MODE = process.env.REACT_APP_PREVIEW_MODE === 'true';

// Main gremlin logo
const GREMLIN_LOGO = 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/d97xwjyk_Logo.png';

// Scroll reveal wrapper
const Reveal = ({ children, direction = 'up', delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  
  const variants = {
    up: { hidden: { opacity: 0, y: 60 }, visible: { opacity: 1, y: 0 } },
    down: { hidden: { opacity: 0, y: -60 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 60 }, visible: { opacity: 1, x: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants[direction]}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Floating particles background
const FloatingParticles = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-purple-500/30 rounded-full"
        style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
        animate={{
          y: [0, -100, 0],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 8 + Math.random() * 4,
          repeat: Infinity,
          delay: Math.random() * 5,
        }}
      />
    ))}
  </div>
);

/* ─── Falling Runes — ancient lore atmosphere (Hero only) ────
   Sparse Roman numerals and runic marks drift slowly downward,
   clustered around the center, cycling through GFG's three logo
   colors (purple · orange · green). Each rune slowly rotates and
   fades away toward the bottom of the hero. */
const FALLING_GLYPHS = [
  // Roman numerals
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XII', 'XX', 'L', 'C', 'D', 'M',
  // Runic / cross marks
  '†', '‡', '☨', '✦', '✧', '❧',
];

// GFG logo colors (purple / orange / green)
const RUNE_COLORS = [
  { fg: '168,85,247' },   // purple
  { fg: '255,140,0' },    // orange
  { fg: '34,197,94' },    // green
];

// Gaussian-ish distribution clustered around 0.5 — three-dice average.
const centerBiased = () => (Math.random() + Math.random() + Math.random()) / 3;

const FallingRunes = () => {
  const runes = useRef(
    [...Array(14)].map((_, i) => {
      const glyph = FALLING_GLYPHS[Math.floor(Math.random() * FALLING_GLYPHS.length)];
      const left = 10 + centerBiased() * 80;
      const color = RUNE_COLORS[i % RUNE_COLORS.length];
      return {
        id: i,
        glyph,
        left,
        size: 18 + Math.random() * 18,
        duration: 22 + Math.random() * 28,
        delay: Math.random() * 25,
        startY: -10 - Math.random() * 20,
        endY: 110 + Math.random() * 10,
        sway: (Math.random() - 0.5) * 40,
        spin: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 360),
        spinDuration: 14 + Math.random() * 16,
        opacity: 0.28 + Math.random() * 0.3,
        italic: Math.random() > 0.6,
        color: color.fg,
      };
    })
  ).current;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden z-[2]"
      aria-hidden
      style={{
        // Soft vertical fade — runes are fully visible near the top and fade
        // into darkness as they approach the bottom of the hero.
        maskImage: 'linear-gradient(to bottom, black 0%, black 55%, rgba(0,0,0,0.5) 80%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 55%, rgba(0,0,0,0.5) 80%, transparent 100%)',
      }}
    >
      {runes.map((r) => (
        <motion.span
          key={r.id}
          className="absolute select-none"
          style={{
            left: `${r.left}%`,
            top: 0,
            fontFamily: "'Cinzel', 'Cinzel Decorative', serif",
            fontSize: `${r.size}px`,
            fontStyle: r.italic ? 'italic' : 'normal',
            fontWeight: 600,
            letterSpacing: '0.04em',
            color: `rgba(${r.color},0.95)`,
            textShadow: `0 0 14px rgba(${r.color},0.7), 0 0 28px rgba(${r.color},0.28)`,
            willChange: 'transform, opacity',
          }}
          initial={{ y: `${r.startY}vh`, x: 0, opacity: 0, rotate: 0 }}
          animate={{
            y: [`${r.startY}vh`, `${r.endY}vh`],
            x: [0, r.sway, -r.sway * 0.6, 0],
            opacity: [0, r.opacity, r.opacity * 0.7, 0],
            rotate: [0, r.spin],
          }}
          transition={{
            y:      { duration: r.duration,     delay: r.delay, repeat: Infinity, ease: 'linear' },
            x:      { duration: r.duration,     delay: r.delay, repeat: Infinity, ease: 'linear' },
            opacity:{ duration: r.duration,     delay: r.delay, repeat: Infinity, ease: 'linear', times: [0, 0.15, 0.5, 0.85] },
            rotate: { duration: r.spinDuration, delay: r.delay, repeat: Infinity, ease: 'linear' },
          }}
        >
          {r.glyph}
        </motion.span>
      ))}
    </div>
  );
};


// Hero Section
const CHANCELLOR_IMG = 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/e41x1kbn_polyyyyyyyy_A_towering_spectral_figure_in_flowing_tattered_ro_26d7b74a-21ea-42cd-b375-a745cf4a43de_1.png';

const ADMIN_WALLET = 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4';

const Hero = () => {
  const navigate = useNavigate();
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();
  const { isDemo, enterDemo } = useDemo();
  const { isLive } = useAppMode();
  const { requestLiveMode, authModalOpen, closeAuthModal } = useLiveMode();
  const { features } = useFeatureFlags();
  const [demoTransition, setDemoTransition] = useState(false);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [logoHovered, setLogoHovered] = useState(false);
  const [logoElevated, setLogoElevated] = useState(false);
  const [showCAModal, setShowCAModal] = useState(false);
  const [caCopied, setCaCopied] = useState(false);
  // Live token contract-address from /api/fair-launch/config — populated after
  // the user sets GFG_TOKEN_MINT (and optionally PUMP_FUN_URL) in backend/.env.
  const [tokenConfig, setTokenConfig] = useState({ token_mint: null, pump_fun_url: '', is_launched: false });
  const logoElevatedTimer = useRef(null);
  const [glitching, setGlitching] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const heroRef = useRef(null);
  const isAdmin = connected && publicKey?.toBase58() === ADMIN_WALLET;
  // Live heartbeat — drives the SUMMON button badge and tab title.
  const heartbeat = useChancellorHeartbeat({ updateTabTitle: true });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Auto-register profile when wallet connects
  useEffect(() => {
    if (connected && publicKey) {
      fetch(`${API_URL}/api/profile/${publicKey.toBase58()}`).catch(() => {});
    }
  }, [connected, publicKey]);

  // Load token contract-address (public endpoint). Refreshes on window focus
  // so the CA flips in as soon as the dev sets GFG_TOKEN_MINT + restarts.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/fair-launch/config`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setTokenConfig({
          token_mint: d.token_mint || null,
          pump_fun_url: d.pump_fun_url || '',
          is_launched: !!d.is_launched,
        });
      } catch { /* silent */ }
    };
    load();
    window.addEventListener('focus', load);
    const t = setInterval(load, 60000);
    return () => { alive = false; window.removeEventListener('focus', load); clearInterval(t); };
  }, []);

  const copyCA = async () => {
    if (!tokenConfig.token_mint) return;
    try {
      await navigator.clipboard.writeText(tokenConfig.token_mint);
      setCaCopied(true);
      setTimeout(() => setCaCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Track mouse position — throttled for performance
  useEffect(() => {
    let rafId;
    const handleMouse = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        if (!heroRef.current) { rafId = null; return; }
        const rect = heroRef.current.getBoundingClientRect();
        setMouse({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
        rafId = null;
      });
    };
    const el = heroRef.current;
    if (el) el.addEventListener('mousemove', handleMouse);
    return () => { if (el) el.removeEventListener('mousemove', handleMouse); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  // Parallax offsets from center (0.5, 0.5)
  const mx = (mouse.x - 0.5) * 2; // -1 to 1
  const my = (mouse.y - 0.5) * 2;

  // Track scroll to slide pipes out (desktop only — pipes are hidden on mobile)
  useEffect(() => {
    if (isMobile) return;
    const handleScroll = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const heroH = rect.height;
      const scrolled = -rect.top;
      setScrollProgress(Math.max(0, Math.min(1, scrolled / (heroH * 0.6))));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Easter egg: logo click triggers glitch + Chancellor tease
  const triggerGlitch = () => {
    if (glitching) return;
    setGlitching(true);
    setLogoHovered(false);
    setTimeout(() => setGlitching(false), 5000);
  };

  // Memoize coin positions so they don't re-randomize on every mouse move
  // Coins removed — replaced by ambient falling runes in the hero.


  return (
    <section ref={heroRef} className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-black to-black" />

      {/* Sewer pipe frames - left and right with edge glow */}
      <div className="absolute left-0 top-0 bottom-0 w-[140px] md:w-[200px] lg:w-[240px] pointer-events-none z-[3] hidden md:block overflow-hidden"
        style={{ transform: `scaleX(-1) translateX(${Math.abs(mx * 3 + scrollProgress * 300)}px)`, transition: 'transform 0.4s ease-out', opacity: 1 - scrollProgress }}>
        <img src="https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/34l4v6hh_polyyyyyyyy_A_vertical_dark_rusty_sewer_pipe_edge_viewed_from_6ed5b4de-f7b7-4d71-a7ce-13ddfd505c7b_0.png"
          alt="" className="h-full w-full object-cover object-left"
          style={{ opacity: 0.55, filter: 'brightness(1.3) saturate(1.4) contrast(1.1)', maskImage: 'linear-gradient(to left, black 35%, transparent 85%), linear-gradient(to top, transparent 0%, black 15%, black 85%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, black 35%, transparent 85%), linear-gradient(to top, transparent 0%, black 15%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskComposite: 'source-in' }} />
        <motion.div className="absolute top-[5%] bottom-[5%] left-[-20px] w-[80px]"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ background: 'linear-gradient(to left, rgba(168,85,247,0.35), rgba(139,92,246,0.15), transparent)', filter: 'blur(15px)' }} />
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-[140px] md:w-[200px] lg:w-[240px] pointer-events-none z-[3] hidden md:block"
        style={{ transform: `translateX(${mx * 3 + scrollProgress * 300}px)`, transition: 'transform 0.4s ease-out', opacity: 1 - scrollProgress }}>
        <img src="https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/yzo5l685_polyyyyyyyy_A_vertical_dark_rusty_sewer_pipe_edge_viewed_from_2b5ba0df-bff5-4578-8f18-7118fe543206_0.png"
          alt="" className="h-full w-full object-cover object-left"
          style={{ opacity: 0.55, filter: 'brightness(1.3) saturate(1.4) contrast(1.1)', maskImage: 'linear-gradient(to left, black 35%, transparent 85%), linear-gradient(to top, transparent 0%, black 15%, black 85%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, black 35%, transparent 85%), linear-gradient(to top, transparent 0%, black 15%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskComposite: 'source-in' }} />
        <motion.div className="absolute top-[5%] bottom-[5%] left-[-20px] w-[80px]"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          style={{ background: 'linear-gradient(to right, rgba(34,197,94,0.3), rgba(168,85,247,0.2), transparent)', filter: 'blur(15px)' }} />
      </div>

      {/* Fog — rectangular, full-height, fading toward center */}
      {/* Left fog — heavy vibrant purple */}
      <motion.div className="absolute left-0 top-0 bottom-0 w-[30%] pointer-events-none z-[2] hidden md:block"
        animate={{ opacity: [0.6, 0.9, 0.65, 0.85, 0.6] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'linear-gradient(to right, rgba(168,85,247,0.2) 0%, rgba(168,85,247,0.08) 40%, transparent 100%)', filter: 'blur(25px)', transform: `translateX(${-scrollProgress * 200}px)`, opacity: 1 - scrollProgress }}
      />
      <motion.div className="absolute left-0 top-0 bottom-0 w-[22%] pointer-events-none z-[2] hidden md:block"
        animate={{ opacity: [0.4, 0.7, 0.45, 0.65, 0.4] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        style={{ background: 'linear-gradient(to right, rgba(139,92,246,0.18) 0%, rgba(168,85,247,0.06) 50%, transparent 100%)', filter: 'blur(30px)', transform: `translateX(${-scrollProgress * 200}px)`, opacity: 1 - scrollProgress }}
      />
      {/* Right fog — green gradient into purple */}
      <motion.div className="absolute right-0 top-0 bottom-0 w-[30%] pointer-events-none z-[2] hidden md:block"
        animate={{ opacity: [0.6, 0.85, 0.65, 0.9, 0.6] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        style={{ background: 'linear-gradient(to left, rgba(34,197,94,0.18) 0%, rgba(168,85,247,0.1) 40%, transparent 100%)', filter: 'blur(25px)', transform: `translateX(${scrollProgress * 200}px)`, opacity: 1 - scrollProgress }}
      />
      <motion.div className="absolute right-0 top-0 bottom-0 w-[22%] pointer-events-none z-[2] hidden md:block"
        animate={{ opacity: [0.4, 0.65, 0.45, 0.7, 0.4] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
        style={{ background: 'linear-gradient(to left, rgba(34,197,94,0.12) 0%, rgba(139,92,246,0.12) 50%, transparent 100%)', filter: 'blur(30px)', transform: `translateX(${scrollProgress * 200}px)`, opacity: 1 - scrollProgress }}
      />

      {/* Ambient Falling Runes — hero-only, Roman numerals + runic marks */}
      <FallingRunes />


      {/* The Den Button - Lore-themed portal to profile, visible to ALL */}
      <motion.button
        onClick={() => navigate('/profile')}
        className="fixed top-6 right-6 z-50 group hidden md:block"
        data-testid="home-profile-btn"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
      >
        <div className="relative flex items-center gap-2.5 px-5 py-2.5 rounded-full overflow-hidden" style={{
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(34,197,94,0.3)',
          boxShadow: '0 0 20px rgba(34,197,94,0.1), inset 0 0 12px rgba(34,197,94,0.05)',
        }}>
          {/* Animated rotating border glow */}
          <motion.div
            className="absolute inset-[-1px] rounded-full pointer-events-none"
            style={{
              background: 'conic-gradient(from 0deg, transparent, rgba(34,197,94,0.4), rgba(0,255,180,0.2), transparent, rgba(168,85,247,0.2), rgba(34,197,94,0.3), transparent)',
              padding: 1,
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), black calc(100% - 1.5px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), black calc(100% - 1.5px))',
            }}
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          />
          {/* Hover glow pulse */}
          <div className="absolute inset-0 rounded-full bg-emerald-500/0 group-hover:bg-emerald-500/10 transition-all duration-500" />
          {/* Pulsing green dot */}
          <div className="relative">
            <motion.div
              className="w-2 h-2 rounded-full"
              style={{ background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.6)' }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
          <span className="relative text-xs font-bold tracking-[0.15em] uppercase" style={{
            fontFamily: "'Cinzel', serif",
            background: 'linear-gradient(90deg, #22c55e, #4ade80, #22c55e)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmer-text 3s linear infinite',
          }}>
            The Den
          </span>
          <ChevronRight className="w-3 h-3 text-emerald-400/60 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all relative" />
        </div>
      </motion.button>
      <style>{`@keyframes shimmer-text { 0% { background-position: 200% center; } 100% { background-position: -200% center; } }`}</style>
      
      <div className="relative text-center max-w-4xl" style={{ zIndex: logoElevated ? 91 : 10 }}>
        {/* === EMERGENT CONTEST BADGE === (hidden) */}
        {false && (
        <Reveal direction="down" delay={0.1}>
          <motion.div
            className="mx-auto mb-6 md:mb-8 inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full"
            style={{
              opacity: logoHovered ? 0 : 1,
              transition: 'opacity 1.2s ease',
              background: 'linear-gradient(135deg, rgba(255,180,40,0.08) 0%, rgba(180,80,20,0.05) 100%)',
              border: '1px solid rgba(255,180,40,0.28)',
              boxShadow: '0 0 18px rgba(255,140,40,0.12)',
            }}
            whileHover={{ scale: 1.03, boxShadow: '0 0 28px rgba(255,140,40,0.22)' }}
            data-testid="hero-contest-badge"
          >
            <motion.span
              className="text-[10px]"
              style={{ color: '#ffaa30' }}
              animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.1, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            >
              ◆
            </motion.span>
            <span className="text-[9px] sm:text-[10px] tracking-[0.32em] uppercase font-bold"
              style={{
                fontFamily: "'Cinzel','Cinzel Decorative',serif",
                background: 'linear-gradient(90deg, #ffd060, #ffaa30, #ffd060)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmer-text 3.5s linear infinite',
              }}>
              Top 10 · Emergent Builder Contest
            </span>
            <motion.span
              className="text-[10px]"
              style={{ color: '#ffaa30' }}
              animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.1, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
            >
              ◆
            </motion.span>
          </motion.div>
        </Reveal>
        )}

        <Reveal direction="scale" delay={0.2}>
          <div className="relative inline-block mb-2 md:mb-3 cursor-pointer"
            style={{
              transform: `perspective(800px) rotateY(${mx * 6}deg) rotateX(${-my * 4}deg)`,
              transition: 'transform 0.3s ease-out',
            }}
            onMouseEnter={() => {
              if (!glitching && !isMobile) {
                setLogoHovered(true);
                setLogoElevated(true);
                if (logoElevatedTimer.current) clearTimeout(logoElevatedTimer.current);
              }
            }}
            onMouseLeave={() => {
              setLogoHovered(false);
              logoElevatedTimer.current = setTimeout(() => setLogoElevated(false), 1400);
            }}
            onClick={() => !isMobile && triggerGlitch()}
            data-testid="logo-easter-egg">
            {/* Pulsing energy ring behind logo */}
            <motion.div className="absolute inset-0 m-auto rounded-full pointer-events-none"
              style={{ width: '110%', height: '110%', top: '-5%', left: '-5%', opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.1, 0.25, 0.1] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
              <div className="w-full h-full rounded-full" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, rgba(34,197,94,0.08) 40%, transparent 70%)' }} />
            </motion.div>

            {/* Glitch layers (desktop only — heavy filter animations) */}
            {!isMobile && (
              <>
                <motion.img src={GREMLIN_LOGO} alt="" aria-hidden="true"
                  className="absolute inset-0 w-44 h-44 sm:w-60 sm:h-60 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] mx-auto object-contain pointer-events-none"
                  style={{ mixBlendMode: 'screen', filter: 'hue-rotate(90deg) saturate(2)', opacity: 0, visibility: logoHovered ? 'hidden' : 'visible' }}
                  animate={{ opacity: [0, 0.4, 0, 0, 0.3, 0], x: [0, -6, 0, 0, 4, 0], clipPath: ['inset(0 0 100% 0)', 'inset(20% 0 40% 0)', 'inset(0 0 100% 0)', 'inset(0 0 100% 0)', 'inset(60% 0 10% 0)', 'inset(0 0 100% 0)'] }}
                  transition={{ duration: 4, repeat: Infinity, repeatDelay: 3 }}
                />
                <motion.img src={GREMLIN_LOGO} alt="" aria-hidden="true"
                  className="absolute inset-0 w-44 h-44 sm:w-60 sm:h-60 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] mx-auto object-contain pointer-events-none"
                  style={{ mixBlendMode: 'screen', filter: 'hue-rotate(-60deg) saturate(3)', opacity: 0, visibility: logoHovered ? 'hidden' : 'visible' }}
                  animate={{ opacity: [0, 0, 0.35, 0, 0, 0.3, 0], x: [0, 0, 5, 0, 0, -4, 0], clipPath: ['inset(0 0 100% 0)', 'inset(0 0 100% 0)', 'inset(50% 0 20% 0)', 'inset(0 0 100% 0)', 'inset(0 0 100% 0)', 'inset(10% 0 70% 0)', 'inset(0 0 100% 0)'] }}
                  transition={{ duration: 4, repeat: Infinity, repeatDelay: 3, delay: 0.1 }}
                />
              </>
            )}

            {/* Main logo — normal state */}
            <motion.img 
              src={GREMLIN_LOGO}
              alt="Gas Free Gremlins"
              className="relative w-44 h-44 sm:w-60 sm:h-60 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] mx-auto object-contain"
              animate={isMobile ? undefined : { 
                x: logoHovered ? [0] : [0, 0, 0, -2, 3, 0, 0, 0, 0, 1, -2, 0, 0, 0],
              }}
              transition={isMobile ? undefined : { duration: 4, repeat: Infinity, repeatDelay: 3 }}
              style={{
                filter: isMobile
                  ? 'drop-shadow(0 0 18px rgba(168,85,247,0.25))'
                  : 'drop-shadow(0 0 30px rgba(168,85,247,0.2)) drop-shadow(0 0 60px rgba(34,197,94,0.1))',
              }}
            />
            {/* White silhouette overlay — fades in on hover */}
            <motion.img
              src={GREMLIN_LOGO}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-44 h-44 sm:w-60 sm:h-60 md:w-80 md:h-80 lg:w-[22rem] lg:h-[22rem] mx-auto object-contain pointer-events-none"
              animate={{ opacity: logoHovered ? 1 : 0 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
              style={{ filter: 'brightness(0) invert(1) drop-shadow(0 0 30px rgba(255,255,255,0.6)) drop-shadow(0 0 60px rgba(255,255,255,0.3)) drop-shadow(0 0 100px rgba(255,255,255,0.15))' }}
            />

            {/* Pulsating glow ring — only on hover */}
            {logoHovered && (
              <>
                <motion.div
                  className="absolute inset-0 m-auto rounded-full pointer-events-none"
                  style={{ width: '115%', height: '115%', top: '-7.5%', left: '-7.5%' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.2, 0.5, 0.2], scale: [0.98, 1.04, 0.98] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <div className="w-full h-full rounded-full" style={{
                    background: 'radial-gradient(circle, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.03) 60%, transparent 75%)',
                    boxShadow: '0 0 60px rgba(255,255,255,0.08), 0 0 120px rgba(255,255,255,0.04)',
                  }} />
                </motion.div>
                {/* "Enter Chancellor's Lair" — clickable indicator */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                >
                  <motion.p
                    className="text-xs sm:text-sm uppercase select-none cursor-pointer"
                    animate={{ 
                      opacity: [0.5, 0.9, 0.5],
                      textShadow: [
                        '0 0 8px rgba(200,30,30,0.3), 0 0 20px rgba(139,0,0,0.15)',
                        '0 0 15px rgba(200,30,30,0.6), 0 0 35px rgba(139,0,0,0.3), 0 0 50px rgba(100,0,0,0.15)',
                        '0 0 8px rgba(200,30,30,0.3), 0 0 20px rgba(139,0,0,0.15)',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                      fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                      fontWeight: 700,
                      color: '#cc2020',
                      letterSpacing: '0.12em',
                    }}
                  >
                    Enter Chancellor's Lair
                  </motion.p>
                </motion.div>
              </>
            )}

            {/* Scan line */}
            <motion.div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full"
              style={{ mixBlendMode: 'overlay', opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}>
              <motion.div className="absolute left-0 right-0 h-[2px]"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.5), rgba(34,197,94,0.3), transparent)' }}
                animate={{ top: ['-10%', '110%'] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}
              />
            </motion.div>

            {/* Corner sparks */}
            {!logoHovered && [...Array(4)].map((_, i) => (
              <motion.div key={i} className="absolute w-1 h-1 rounded-full pointer-events-none"
                style={{ background: i % 2 === 0 ? '#a855f7' : '#22c55e', top: i < 2 ? '15%' : '75%', left: i % 2 === 0 ? '10%' : '85%' }}
                animate={{ scale: [0, 1.5, 0], opacity: [0, 1, 0] }}
                transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 3 + i * 0.8, delay: i * 0.5 }}
              />
            ))}
          </div>
        </Reveal>

        <Reveal direction="up" delay={0.4}>
          <div className="mb-8 md:mb-10 max-w-2xl mx-auto px-2" style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}>
            <motion.p className="text-base sm:text-xl md:text-2xl font-light"
              style={{ color: 'rgba(255,255,255,0.5)' }}>
              {"Play. Burn. Compete.".split("").map((char, i) => (
                <motion.span key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.03, duration: 0.3 }}
                  style={{ color: char === '.' ? '#a855f7' : undefined }}
                >{char}</motion.span>
              ))}
            </motion.p>
            <motion.p className="text-sm sm:text-base md:text-lg mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              style={{ color: 'rgba(255,255,255,0.25)' }}>
              The first meme coin that gets better every season.
            </motion.p>
          </div>
        </Reveal>

        <Reveal direction="up" delay={0.5}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}>
            {/* Demo Button — PRIMARY CTA for non-connected users (contest voters land here first) */}
            {((!connected && !isDemo) || (isAdmin && !isDemo)) && (
              <motion.button onClick={() => setDemoTransition(true)}
                className="group relative overflow-hidden rounded-xl hover:scale-[1.04] active:scale-[0.97] transition-all duration-300 order-1 sm:order-1"
                data-testid="home-demo-btn"
                whileHover={{ boxShadow: '0 0 50px rgba(168,85,247,0.4), 0 0 100px rgba(57,255,20,0.15)' }}>
                <motion.div className="absolute inset-0 rounded-xl"
                  style={{ background: 'conic-gradient(from 0deg, #a855f7, #39ff14, #c084fc, #39ff14, #a855f7)', padding: '1.5px' }}
                  animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}>
                  <div className="w-full h-full rounded-xl" style={{ background: '#0a0015' }} />
                </motion.div>
                <div className="absolute inset-[2px] rounded-[10px] overflow-hidden">
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(168,85,247,0.18), transparent 70%)' }} />
                  <motion.div className="absolute inset-0"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(57,255,20,0.1), rgba(168,85,247,0.12), transparent)', backgroundSize: '200% 100%' }}
                    animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
                </div>
                <div className="relative z-10 flex items-center gap-3 px-9 py-4">
                  <motion.div animate={{ scale: [1, 1.18, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}>
                    <Eye className="w-5 h-5" style={{ color: '#c084fc', filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.7))' }} />
                  </motion.div>
                  <span className="text-sm tracking-[0.18em] uppercase font-bold"
                    style={{ fontFamily: "'Cinzel', serif", background: 'linear-gradient(90deg, #c084fc, #e9d5ff, #39ff14)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.4))' }}>
                    Play The Demo
                  </span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: '#39ff14', filter: 'drop-shadow(0 0 4px rgba(57,255,20,0.5))' }} />
                </div>
              </motion.button>
            )}

            {/* LIVE MODE — hidden entirely in PREVIEW_MODE; verification modal gates authorized wallets otherwise */}
            {!isDemo && !isLive && !PREVIEW_MODE && (
              <motion.button
                onClick={requestLiveMode}
                data-testid="home-live-btn"
                className="group relative overflow-hidden rounded-xl hover:scale-[1.04] active:scale-[0.97] transition-all duration-300 order-1 sm:order-1"
                whileHover={{ boxShadow: '0 0 50px rgba(125,208,255,0.45), 0 0 100px rgba(120,80,200,0.18)' }}
              >
                <motion.div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background: 'conic-gradient(from 90deg, #7dd0ff, #a892ff, #5be584, #7dd0ff)',
                    padding: '1.5px',
                  }}
                  animate={{ rotate: -360 }}
                  transition={{ duration: 9, repeat: Infinity, ease: 'linear' }}
                >
                  <div className="w-full h-full rounded-xl" style={{ background: '#04080f' }} />
                </motion.div>
                <div className="absolute inset-[2px] rounded-[10px] overflow-hidden">
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(125,208,255,0.18), transparent 70%)' }} />
                </div>
                <div className="relative z-10 flex items-center gap-3 px-9 py-4">
                  <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}>
                    <span
                      className="inline-block rounded-full"
                      style={{
                        width: 8, height: 8,
                        background: '#7dd0ff',
                        boxShadow: '0 0 10px #7dd0ff, 0 0 18px rgba(125,208,255,0.5)',
                      }}
                    />
                  </motion.div>
                  <span
                    className="text-sm tracking-[0.18em] uppercase font-bold"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      background: 'linear-gradient(90deg, #7dd0ff, #c5e7ff, #a892ff)',
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 6px rgba(125,208,255,0.5))',
                    }}
                  >
                    Enter Live Mode
                  </span>
                  <span
                    className="text-[9px] tracking-[0.28em] uppercase font-mono px-1.5 py-px rounded-sm"
                    style={{
                      color: 'rgba(125,208,255,0.95)',
                      background: 'rgba(125,208,255,0.10)',
                      border: '1px solid rgba(125,208,255,0.45)',
                    }}
                  >DEVNET</span>
                </div>
              </motion.button>
            )}

            {/* Halls of the Chancellor — primary lore CTA, replaces the old "$GFG · Coming Soon" placeholder */}
            <button
              onClick={() => navigate('/halls')}
              data-testid="hero-halls-btn"
              className="group relative overflow-hidden hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 order-2 sm:order-2"
            >
              <div className="absolute inset-0 rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 50%, rgba(60,18,12,0.65) 100%)',
                  border: '1px solid rgba(200,144,72,0.45)',
                  boxShadow: '0 0 22px rgba(200,144,72,0.25), inset 0 0 22px rgba(0,0,0,0.5)',
                }}
              />
              {/* Crimson radial veil — animated */}
              <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
                animate={{ opacity: [0.4, 0.85, 0.4] }}
                transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(220,40,40,0.18), transparent 65%)' }}
              />
              <div className="relative z-10 flex items-center gap-3 px-9 py-4">
                <span style={{ color: '#c89048', fontSize: '14px' }}>†</span>
                <span className="text-sm tracking-[0.18em] uppercase font-bold"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    color: '#ffd680',
                    textShadow: '0 0 12px rgba(200,144,72,0.55)',
                  }}>
                  Summon the Chancellor
                </span>
                <span className="text-[9px] tracking-[0.28em] uppercase font-mono px-1.5 py-px rounded-sm inline-flex items-center gap-1"
                  style={{
                    color: 'rgba(140,220,140,0.95)',
                    background: 'rgba(57,255,20,0.10)',
                    border: '1px solid rgba(57,255,20,0.35)',
                  }}>
                  <motion.span
                    className="inline-block rounded-full"
                    style={{ width: 5, height: 5, background: '#5be584', boxShadow: '0 0 6px #5be584' }}
                    animate={{ opacity: [0.45, 1, 0.45] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  {heartbeat.ready
                    ? `Live · ${heartbeat.soulsWatched} ${heartbeat.soulsWatched === 1 ? 'soul' : 'souls'}`
                    : 'Live'}
                </span>
                <span style={{ color: '#c89048', fontSize: '14px' }}>†</span>
              </div>
            </button>

            {/* Connect Wallet when disconnected, Enter Heist when connected */}
            {connected ? (
              <button
                onClick={() => navigate('/arcade')}
                className="group relative overflow-hidden hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                data-testid="home-arcade-btn"
              >
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(ellipse, rgba(255,140,0,0.15), transparent 70%)', filter: 'blur(15px)' }} />
                <div className="relative flex items-center gap-3 px-8 py-4 backdrop-blur-sm rounded-xl transition-all duration-300"
                  style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,140,0,0.3)' }}>
                  <Gamepad2 className="w-5 h-5" style={{ color: '#ff8c00' }} />
                  <span className="text-sm font-bold tracking-wider uppercase" style={{ fontFamily: "'Cinzel', serif", background: 'linear-gradient(90deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Descend Into The Heist
                  </span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-all" style={{ color: '#ff8c00' }} />
                </div>
              </button>
            ) : features.wallet_connect && !isDemo ? (
              <button
                onClick={() => setVisible(true)}
                className="group relative overflow-hidden hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 order-3 sm:order-3"
                data-testid="home-connect-wallet-btn"
              >
                <div className="relative flex items-center gap-3 px-7 py-3.5 bg-black/50 backdrop-blur-sm border border-purple-500/20 hover:border-purple-400/45 rounded-xl transition-all duration-300">
                  <Wallet className="w-4 h-4 text-purple-400/80" />
                  <span className="text-xs font-medium text-white/60 group-hover:text-white/85 transition-colors uppercase tracking-wider">Connect Wallet</span>
                </div>
              </button>
            ) : null}
          </div>
        </Reveal>

        {/* === CONTEST CONVERSION STRIP — trust signals for first-time visitors === */}
        <Reveal direction="up" delay={0.62}>
          <div className="mt-4 md:mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 items-center"
            style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}
            data-testid="hero-trust-strip">
            {/* No wallet required */}
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#39ff14', boxShadow: '0 0 8px rgba(57,255,20,0.7)' }} />
              <span className="text-[10px] md:text-[11px] font-mono tracking-[0.18em] uppercase" style={{ color: 'rgba(220,255,200,0.65)' }}>
                Playable Demo · No Wallet Required
              </span>
            </div>
            {/* Floors count */}
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#ff8c00', boxShadow: '0 0 8px rgba(255,140,0,0.7)' }} />
              <span className="text-[10px] md:text-[11px] font-mono tracking-[0.18em] uppercase" style={{ color: 'rgba(255,220,180,0.65)' }}>
                10 Cinematic Floors
              </span>
            </div>
            {/* Genesis Passport */}
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#a855f7', boxShadow: '0 0 8px rgba(168,85,247,0.7)' }} />
              <span className="text-[10px] md:text-[11px] font-mono tracking-[0.18em] uppercase" style={{ color: 'rgba(220,200,255,0.65)' }}>
                Earn a Genesis Passport
              </span>
            </div>
          </div>
        </Reveal>

        {/* CA Notice / Live Contract Address */}
        <Reveal direction="up" delay={0.58}>
          <div className="mt-4 md:mt-5 flex justify-center" style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }}>
            {tokenConfig.token_mint ? (
              // ─── LIVE: token has launched, show CA + copy + explorer + Pump.fun ───
              <div className="relative flex flex-col md:flex-row items-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 max-w-[92vw]" data-testid="ca-live"
                style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.35)', boxShadow: '0 0 25px rgba(57,255,20,0.08)' }}>
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l" style={{ borderColor: 'rgba(57,255,20,0.5)' }} />
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r" style={{ borderColor: 'rgba(57,255,20,0.5)' }} />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l" style={{ borderColor: 'rgba(57,255,20,0.5)' }} />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r" style={{ borderColor: 'rgba(57,255,20,0.5)' }} />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <motion.span className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: '#39ff14', boxShadow: '0 0 8px rgba(57,255,20,0.85)' }}
                    animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.6, repeat: Infinity }} />
                  <span className="text-[9px] md:text-[10px] tracking-[0.25em] uppercase font-bold"
                    style={{ fontFamily: "'Cinzel', serif", color: '#39ff14' }}>$GFG CA</span>
                </div>
                <button onClick={copyCA} data-testid="ca-copy-btn"
                  className="group flex-1 min-w-0 flex items-center justify-center md:justify-start gap-2 px-2 md:px-3 py-1 transition"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(57,255,20,0.2)' }}>
                  <span className="text-[10px] md:text-xs font-mono truncate"
                    style={{ color: 'rgba(220,255,220,0.85)', maxWidth: isMobile ? '58vw' : '340px' }}>
                    {tokenConfig.token_mint}
                  </span>
                  <span className="text-[9px] font-mono flex-shrink-0"
                    style={{ color: caCopied ? '#39ff14' : 'rgba(220,255,220,0.5)' }}>
                    {caCopied ? '✓ COPIED' : 'COPY'}
                  </span>
                </button>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <a href={`https://solscan.io/token/${tokenConfig.token_mint}`}
                    target="_blank" rel="noreferrer" data-testid="ca-solscan-btn"
                    className="px-2 py-1 text-[10px] tracking-wider uppercase hover:text-green-300 transition"
                    style={{ fontFamily: "'Courier New', monospace", background: 'rgba(0,0,0,0.4)',
                             border: '1px solid rgba(57,255,20,0.2)', color: 'rgba(220,255,220,0.65)' }}>
                    Solscan ↗
                  </a>
                  {tokenConfig.pump_fun_url ? (
                    <a href={tokenConfig.pump_fun_url} target="_blank" rel="noreferrer" data-testid="ca-pumpfun-btn"
                      className="px-2 py-1 text-[10px] tracking-wider uppercase font-bold hover:brightness-125 transition"
                      style={{ fontFamily: "'Courier New', monospace",
                               background: 'linear-gradient(90deg, rgba(234,179,8,0.15), rgba(234,179,8,0.08))',
                               border: '1px solid rgba(234,179,8,0.5)', color: '#eab308' }}>
                      Pump.fun ↗
                    </a>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                {/* Mobile: compact CA chip → opens modal */}
                <button
                  onClick={() => setShowCAModal(true)}
                  data-testid="ca-chip"
                  className="md:hidden relative flex items-center gap-2 px-3 py-1.5 transition-all active:scale-95"
                  style={{
                    background: 'rgba(168,85,247,0.08)',
                    border: '1px solid rgba(168,85,247,0.3)',
                    boxShadow: '0 0 12px rgba(168,85,247,0.08)',
                  }}
                >
                  <motion.div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.7)' }}
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity }} />
                  <span className="text-[10px] tracking-[0.25em] uppercase font-bold"
                    style={{ fontFamily: "'Courier New', monospace", color: '#c084fc' }}>
                    CA Notice
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(192,132,252,0.55)' }}>›</span>
                </button>

                {/* Desktop: full notice */}
                <div className="hidden md:flex relative overflow-hidden items-center gap-3 px-6 py-3" data-testid="ca-notice"
                  style={{ background: 'rgba(168,85,247,0.03)', border: '1px solid rgba(168,85,247,0.15)', boxShadow: '0 0 20px rgba(168,85,247,0.04)' }}>
                  {/* Corner brackets */}
                  <div className="absolute top-0 left-0 w-2 h-2 border-t border-l" style={{ borderColor: 'rgba(168,85,247,0.25)' }} />
                  <div className="absolute top-0 right-0 w-2 h-2 border-t border-r" style={{ borderColor: 'rgba(57,255,20,0.2)' }} />
                  <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l" style={{ borderColor: 'rgba(57,255,20,0.2)' }} />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r" style={{ borderColor: 'rgba(168,85,247,0.25)' }} />
                  <motion.div className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: '#a855f7', boxShadow: '0 0 8px rgba(168,85,247,0.6)' }}
                    animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} />
                  <p className="text-[10px] md:text-[11px] tracking-wide" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(200,180,220,0.5)' }}>
                    The official $GFG contract address will <span style={{ color: '#c084fc', fontWeight: 'bold', filter: 'drop-shadow(0 0 4px rgba(168,85,247,0.4))' }}>ONLY</span> be announced through our official channels. Do not trust any other sources.
                  </p>
                </div>
              </>
            )}
          </div>
        </Reveal>

        <Reveal direction="up" delay={0.65}>
          <motion.div className="mt-3 md:mt-4 flex flex-col items-center gap-2 cursor-pointer"
            style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 1.2s ease' }} 
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            data-testid="scroll-indicator">
            <motion.span className="text-xs font-mono tracking-[0.3em] uppercase"
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ color: 'rgba(168,85,247,0.5)' }}>
              Scroll to explore
            </motion.span>
            <div className="relative w-6 h-10 rounded-full flex justify-center" style={{ border: '2px solid rgba(168,85,247,0.25)' }}>
              <motion.div 
                className="w-1.5 h-1.5 rounded-full mt-2"
                animate={{ y: [0, 16, 0], opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{ background: 'linear-gradient(180deg, #a855f7, #ff6b00)' }}
              />
            </div>
            <motion.div className="flex flex-col items-center gap-0.5 mt-1"
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}>
              <ChevronRight className="w-4 h-4 rotate-90" style={{ color: 'rgba(168,85,247,0.4)' }} />
              <ChevronRight className="w-4 h-4 rotate-90 -mt-2.5" style={{ color: 'rgba(168,85,247,0.2)' }} />
            </motion.div>
          </motion.div>
        </Reveal>
      </div>

      {/* Easter Egg: Hover — pure black void */}
      <AnimatePresence>
        {logoHovered && !glitching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            className="fixed inset-0 z-[90] pointer-events-none"
            style={{ background: '#000' }}
          />
        )}
      </AnimatePresence>

      {/* CA Trust Notice Modal (mobile tap) */}
      <AnimatePresence>
        {showCAModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[210] flex items-center justify-center px-6"
            style={{ background: 'rgba(2,0,6,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowCAModal(false)}
            data-testid="ca-modal-backdrop"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md p-6"
              style={{
                background: 'linear-gradient(135deg, rgba(20,10,30,0.96), rgba(14,6,20,0.96))',
                border: '1px solid rgba(168,85,247,0.4)',
                boxShadow: '0 0 60px rgba(168,85,247,0.2), 0 0 120px rgba(168,85,247,0.05)',
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="ca-modal"
            >
              {/* Corner brackets */}
              <div className="absolute top-2 left-2 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(168,85,247,0.5)' }} />
              <div className="absolute top-2 right-2 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(57,255,20,0.4)' }} />
              <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(57,255,20,0.4)' }} />
              <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(168,85,247,0.5)' }} />

              <div className="flex items-center gap-2 mb-3">
                <motion.div className="w-2 h-2 rounded-full"
                  style={{ background: '#a855f7', boxShadow: '0 0 10px rgba(168,85,247,0.8)' }}
                  animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity }} />
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: '#c084fc' }}>
                  Contract Address Notice
                </span>
              </div>

              <h3 className="text-xl font-bold mb-3" style={{
                fontFamily: "'Cinzel', serif",
                background: 'linear-gradient(90deg, #c084fc, #39ff14)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Beware of Scammers
              </h3>

              <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'rgba(220,210,240,0.8)' }}>
                The official <span style={{ color: '#c084fc', fontWeight: 700 }}>$GFG contract address</span> will <span style={{ color: '#39ff14', fontWeight: 700 }}>ONLY</span> be announced through our official channels.
              </p>

              <p className="text-[12px] leading-relaxed mb-5" style={{ color: 'rgba(200,180,220,0.6)' }}>
                Do not trust any CA posted in DMs, replies, or unofficial groups. Anyone selling $GFG before the official launch is a scammer.
              </p>

              <div className="flex items-center gap-2 p-2.5 mb-4" style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.2)' }}>
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#39ff14' }}>
                  Official channels:
                </span>
                <span className="text-[11px]" style={{ color: 'rgba(220,210,240,0.7)' }}>X + gasfreegremlins.com</span>
              </div>

              <button
                onClick={() => setShowCAModal(false)}
                data-testid="ca-modal-close"
                className="w-full py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold transition-all active:scale-[0.98]"
                style={{
                  fontFamily: "'Cinzel', serif",
                  color: '#c084fc',
                  background: 'rgba(168,85,247,0.1)',
                  border: '1px solid rgba(168,85,247,0.35)',
                }}
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Easter Egg: THE CHANCELLOR — full cinematic horror sequence */}
      <AnimatePresence>
        {glitching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 1.5, ease: 'easeInOut' } }}
            transition={{ duration: 0.05 }}
            className="fixed inset-0 z-[200] overflow-hidden cursor-none select-none"
            style={{ background: '#000', willChange: 'opacity' }}
          >
            {/* === PHASE 1: White flash (0-0.6s) === */}
            <motion.div className="absolute inset-0"
              initial={{ opacity: 1 }}
              animate={{ opacity: [1, 0.8, 0] }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ background: '#fff', mixBlendMode: 'screen', willChange: 'opacity' }} />

            {/* Screen shake container */}
            <motion.div className="w-full h-full relative"
              style={{ willChange: 'transform' }}
              animate={{ x: [0, -10, 12, -6, 8, 0], y: [0, 6, -8, 4, -5, 0] }}
              transition={{ duration: 0.6, ease: 'linear' }}>

              {/* CRT scanlines — pure CSS, no animation */}
              <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.03) 1px, rgba(255,255,255,0.03) 3px)',
                backgroundSize: '100% 4px', zIndex: 10,
              }} />

              {/* Chromatic aberration — 2 layers only */}
              <motion.div className="absolute inset-0"
                style={{ background: 'rgba(255,0,0,0.06)', mixBlendMode: 'screen', zIndex: 5, willChange: 'transform, opacity' }}
                animate={{ x: [-5, 6, -3, 0], opacity: [0, 0.3, 0.15, 0] }}
                transition={{ duration: 1, delay: 0.3 }} />
              <motion.div className="absolute inset-0"
                style={{ background: 'rgba(0,0,200,0.05)', mixBlendMode: 'screen', zIndex: 5, willChange: 'transform, opacity' }}
                animate={{ x: [5, -6, 3, 0], opacity: [0, 0.25, 0.1, 0] }}
                transition={{ duration: 1, delay: 0.35 }} />

              {/* VHS tracking bars — reduced to 4 */}
              {[0, 1, 2, 3].map((i) => (
                <motion.div key={`vhs-${i}`} className="absolute left-0 right-0 pointer-events-none"
                  style={{ background: i % 2 === 0 ? 'rgba(139,0,0,0.2)' : 'rgba(168,85,247,0.12)',
                    top: `${15 + i * 22}%`, height: '1px', zIndex: 8, willChange: 'opacity' }}
                  animate={{ opacity: [0, 0.5, 0, 0.3, 0] }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.08, repeat: 4 }} />
              ))}

              {/* === Chancellor emerges === */}
              <motion.div className="absolute inset-0 flex items-center justify-center"
                style={{ zIndex: 3, willChange: 'transform, opacity' }}
                initial={{ opacity: 0, scale: 1.3 }}
                animate={{ opacity: [0, 0, 0.1, 0.3, 0.7, 0.8, 0.5, 0.2, 0], scale: [1.3, 1.2, 1.15, 1.08, 1.02, 1, 1.02, 1.06, 1.1] }}
                transition={{ duration: 4.5, times: [0, 0.1, 0.2, 0.3, 0.42, 0.52, 0.68, 0.82, 1] }}>
                <img src={CHANCELLOR_IMG} alt="" className="w-full h-full object-cover"
                  style={{ filter: 'contrast(1.4) brightness(0.5) saturate(1.5)', mixBlendMode: 'screen' }} />
              </motion.div>

              {/* Red vignette */}
              <motion.div className="absolute inset-0"
                style={{ background: 'radial-gradient(ellipse at center, transparent 20%, rgba(80,0,0,0.5) 70%, rgba(40,0,0,0.8) 100%)', zIndex: 6, willChange: 'opacity' }}
                animate={{ opacity: [0, 0.2, 0.6, 0.5, 0.2, 0] }}
                transition={{ duration: 4.5, times: [0, 0.15, 0.4, 0.6, 0.8, 1] }} />

              {/* === Text === */}
              <motion.div className="absolute inset-0 flex flex-col items-center justify-center" style={{ zIndex: 20 }}>
                <motion.p
                  style={{ color: 'rgba(139,0,0,0.4)', willChange: 'opacity' }}
                  animate={{ opacity: [0, 0, 0, 0.6, 0.5, 0.2, 0] }}
                  transition={{ duration: 4.5, times: [0, 0.25, 0.32, 0.38, 0.5, 0.65, 1] }}
                  className="text-xs sm:text-sm font-mono tracking-[0.4em] uppercase mb-6">
                  you were warned
                </motion.p>

                <motion.h1
                  style={{
                    fontFamily: "'Cinzel Decorative', 'Cinzel', serif", fontWeight: 700,
                    color: 'rgba(180,20,20,0.9)',
                    textShadow: '0 0 40px rgba(139,0,0,0.6), 0 0 80px rgba(80,0,0,0.4), 0 2px 4px rgba(0,0,0,0.8)',
                    letterSpacing: '0.08em', willChange: 'transform, opacity',
                  }}
                  initial={{ opacity: 0, scale: 1.6 }}
                  animate={{
                    opacity: [0, 0, 0, 0, 0.9, 1, 0.8, 0.5, 0.2, 0],
                    scale: [1.6, 1.6, 1.6, 1.6, 1.02, 1, 1.01, 1.03, 1.06, 1.1],
                  }}
                  transition={{ duration: 4.5, times: [0, 0.1, 0.2, 0.3, 0.36, 0.46, 0.58, 0.7, 0.85, 1] }}
                  className="text-3xl sm:text-5xl md:text-7xl uppercase text-center leading-tight">
                  The Chancellor<br/>
                  <span style={{ fontSize: '0.6em', letterSpacing: '0.2em', color: 'rgba(200,30,30,0.7)' }}>is coming</span>
                </motion.h1>

                <motion.div
                  style={{ height: 2, background: 'linear-gradient(90deg, transparent, rgba(139,0,0,0.6), rgba(200,0,0,0.4), rgba(139,0,0,0.6), transparent)', willChange: 'width' }}
                  initial={{ width: 0 }}
                  animate={{ width: [0, 0, 0, 0, 300, 300, 250, 100, 0] }}
                  transition={{ duration: 4.5, times: [0, 0.1, 0.2, 0.33, 0.4, 0.55, 0.7, 0.85, 1] }}
                  className="mt-4" />

                <motion.p
                  style={{ fontFamily: "'Cinzel', serif", color: 'rgba(120,0,0,0.4)', fontStyle: 'italic', willChange: 'opacity' }}
                  animate={{ opacity: [0, 0, 0, 0, 0, 0.5, 0.4, 0.15, 0] }}
                  transition={{ duration: 4.5, times: [0, 0.2, 0.3, 0.4, 0.5, 0.56, 0.65, 0.8, 1] }}
                  className="text-xs sm:text-sm tracking-[0.3em] uppercase mt-6">
                  he sees everything
                </motion.p>
              </motion.div>

              {/* Film grain — single CSS layer, no animation */}
              <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                zIndex: 15,
              }} />

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Demo glitch transition */}
      <DemoTransition active={demoTransition} onComplete={() => { localStorage.setItem('gfg_onboarded', 'true'); enterDemo(); window.scrollTo(0, 0); setDemoTransition(false); }} />
      <LiveModeAuthModal open={authModalOpen} onClose={closeAuthModal} />

    </section>
  );
};

// Project Explainer — Transparent, detailed, honest
const ProjectExplainer = () => {
  const navigate = useNavigate();

  const mechanics = [
    {
      id: 'free-demo',
      numeral: 'I',
      icon: Unlock,
      title: 'No Wallet, No Wait',
      hook: 'You can play the whole product before paying a cent.',
      detail: 'Every arcade game, every Chancellor reading, every leaderboard works in demo mode. Connect a wallet only when you want to mint your Genesis Passport or burn real $GFG. The full experience is unlocked from the moment you arrive.',
      color: '#22c55e',
      colorRGB: '34,197,94',
      tag: 'Free Demo',
      decree: 'Lex Praesens · The Open Door',
    },
    {
      id: 'antiwhale',
      numeral: 'II',
      icon: Shield,
      title: 'Skill Over Spending',
      hook: 'Whales burn millions. Grinders win.',
      detail: 'Burns scale logarithmically, so a million tokens earns thirteen times more SP than a thousand, not a thousand times more. Burn SP is also capped at three times your arcade SP. Money alone cannot buy a leaderboard slot.',
      color: '#a855f7',
      colorRGB: '168,85,247',
      tag: 'Fair Play',
      decree: 'Iudicium Aequum · The Even Scales',
    },
    {
      id: 'chancellor',
      numeral: 'III',
      icon: Eye,
      title: 'The Chancellor Watches',
      hook: 'An autonomous AI judges wallets in real time.',
      detail: 'The Chancellor reads any Solana wallet on demand, narrates live trades from a public watchlist, detects cross-wallet coordination between souls, and signs every word he speaks into a permanent on-chain Codex. He never forgets a wallet he has read.',
      color: '#dc2626',
      colorRGB: '220,38,38',
      tag: 'Live AI',
      decree: 'Oculus Vigilans · The Watching Eye',
    },
    {
      id: 'burns',
      numeral: 'IV',
      icon: Flame,
      title: 'Permanent Burns',
      hook: 'Supply only goes down. Forever.',
      detail: 'Every burn happens on-chain through the SPL Token program, so tokens vanish from circulation permanently. Each thirty day season tightens the float and pays back the holders who stayed through the last one. The math compounds.',
      color: '#ff6b00',
      colorRGB: '255,107,0',
      tag: 'Deflation',
      decree: 'Lex Cinis · The Law of Ash',
    },
    {
      id: 'graded',
      numeral: 'V',
      icon: Target,
      title: 'Graded in Public',
      hook: 'Every prediction is checked against reality.',
      detail: 'When the Chancellor calls a token bullish or warns one is a rug, that call locks into a public ledger. Twenty-four hours later, it is graded against actual on-chain price action. The accuracy stat is permanent, visible to anyone, and impossible to spin.',
      color: '#06b6d4',
      colorRGB: '6,182,212',
      tag: 'Track Record',
      decree: 'Veritas Probata · The Tested Word',
    },
    {
      id: 'genesis',
      numeral: 'VI',
      icon: Crown,
      title: 'The Genesis Passport',
      hook: 'Demo souls only. Mint closes when the token launches.',
      detail: 'The Genesis Passport is minted only for souls who played the demo and earned their place in the ledger before launch. Active demo players who grind Season Points, complete quests, and show up while the arcade is still free are the only ones eligible. Miss the demo and the door closes forever.',
      color: '#ffd700',
      colorRGB: '255,215,0',
      tag: 'Limited Mint',
      decree: 'Pondus Originis · The Weight of Beginning',
    },
  ];

  return (
    <section className="py-16 md:py-28 px-4 sm:px-6 relative overflow-x-clip" data-testid="how-it-works-section">
      {/* Ambient background (heavy blur — desktop only) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/[0.03] to-black" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header — What is $GFG */}
        <Reveal>
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.12)' }}>
              <Shield className="w-3.5 h-3.5" style={{ color: '#a855f7' }} />
              <span className="text-[10px] font-mono tracking-wider uppercase" style={{ color: '#a855f7' }}>Full Transparency</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white mb-4">
              What is <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-orange-400">$GFG</span>?
            </h2>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-3 px-2">
              A Solana meme coin built around 30-day Seasons. Play free arcade games to earn Season Points. Burn $GFG to compete for monthly community rewards. Supply shrinks every season.
            </p>
            <p className="text-gray-500 text-xs md:text-sm max-w-xl mx-auto px-2">
              Two currencies keep it fair: free in-game credits for playing, real $GFG tokens for burning. They never mix.
            </p>
          </div>
        </Reveal>

        {/* Divider */}
        <Reveal delay={0.1}>
          <div className="flex items-center justify-center gap-3 my-8">
            <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.2))' }} />
            <p className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: 'rgba(168,85,247,0.3)' }}>The Engine</p>
            <div className="h-[1px] w-16" style={{ background: 'linear-gradient(270deg, transparent, rgba(168,85,247,0.2))' }} />
          </div>
        </Reveal>

        {/* Mechanic Cards — scroll-driven horizontal carousel ("Codex of Decrees") */}
        <MechanicsCarousel mechanics={mechanics} />
      </div>
    </section>
  );
};

/* ─── Mechanics Carousel ──────────────────────────────────────
   As the user scrolls, the section pins to the viewport and the
   six cards slide horizontally. Each card gets ~1 viewport-height
   of vertical scroll. Top progress bar + Roman-numeral counter
   make it feel like turning the pages of a court ledger. */
const MechanicsCarousel = ({ mechanics }) => {
  const N = mechanics.length;
  const trackRef = useRef(null);
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Build "dwell + transition" waypoints so each card sits centered for most
  // of its slice of scroll, with a short transition between them.
  // For N cards we get N dwell intervals separated by (N-1) transitions.
  // Each card occupies 1/N of total scroll; ~70% dwell, ~30% transition.
  const { input, output } = useMemo(() => {
    const inp = [0];
    const out = ['0vw'];
    const slice = 1 / N;          // fraction of scroll per card
    const dwellHalf = slice * 0.35; // half-width of dwell around each card centre
    for (let i = 0; i < N; i++) {
      const centre = (i + 0.5) * slice; // 0.083, 0.25, 0.416, ...
      const dwellStart = Math.max(0, centre - dwellHalf);
      const dwellEnd = Math.min(1, centre + dwellHalf);
      const offsetVw = `${-i * 100}vw`;
      // Push start and end of dwell — both at the same x for plateau
      if (i === 0) {
        inp.push(dwellEnd);
        out.push(offsetVw);
      } else {
        inp.push(dwellStart, dwellEnd);
        out.push(offsetVw, offsetVw);
      }
    }
    // Ensure final point is 1
    if (inp[inp.length - 1] < 1) {
      inp.push(1);
      out.push(`${-(N - 1) * 100}vw`);
    }
    return { input: inp, output: out };
  }, [N]);

  const x = useTransform(scrollYProgress, input, output);
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  // Subtle parallax for the giant ghost numeral (drifts up while scrolling)
  const numeralY = useTransform(scrollYProgress, [0, 1], ['8%', '-8%']);
  const numeralRotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);
  // Parallax for the slow drifting diamond watermarks
  const bgFarY    = useTransform(scrollYProgress, [0, 1], ['18%', '-18%']);
  const bgFarX    = useTransform(scrollYProgress, [0, 1], ['-4%', '4%']);

  // Track which card is currently centered for the indicator (uses dwell midpoints)
  const [activeIdx, setActiveIdx] = useState(0);
  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const i = Math.min(N - 1, Math.max(0, Math.floor(latest * N)));
    if (i !== activeIdx) setActiveIdx(i);
  });

  return (
    <div
      ref={containerRef}
      className="relative w-screen left-1/2 -translate-x-1/2"
      style={{ height: `${N * 100}vh` }}
      data-testid="mechanics-carousel"
    >
      {/* Pinned viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Soft radial accent that picks up the active card's color */}
        <motion.div
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            background: mechanics[activeIdx]
              ? `radial-gradient(ellipse at 50% 50%, rgba(${mechanics[activeIdx].colorRGB},0.10) 0%, transparent 55%)`
              : 'transparent',
          }}
        />

        {/* ─── ATMOSPHERE LAYER ─────────────────────────────
           Giant ghost numeral · embers · grain · corner sigils */}

        {/* Giant ghost Roman numeral — fills negative space, color-tinted */}
        <motion.div
          className="absolute inset-0 pointer-events-none flex items-center justify-center select-none"
          style={{ y: numeralY, rotate: numeralRotate }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={mechanics[activeIdx]?.id}
              initial={{ opacity: 0, scale: 0.85, filter: 'blur(40px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, scale: 1.1, filter: 'blur(60px)' }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                fontFamily: "'Cinzel', serif",
                fontWeight: 700,
                fontSize: 'clamp(16rem, 60vh, 56rem)',
                lineHeight: 1,
                color: 'transparent',
                WebkitTextStroke: `1px rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.07)`,
                textShadow: `0 0 120px rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.06)`,
                letterSpacing: '-0.05em',
              }}
            >
              {mechanics[activeIdx]?.numeral}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Drifting ember motes — color-tinted to active card */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(22)].map((_, i) => {
            const left = (i * 37 + 11) % 100;
            const size = 1.5 + ((i * 7) % 5);
            const delay = (i * 0.7) % 8;
            const duration = 9 + ((i * 3) % 7);
            const drift = ((i * 13) % 40) - 20;
            return (
              <motion.span
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${left}%`,
                  bottom: '-10px',
                  width: `${size}px`,
                  height: `${size}px`,
                  background: `rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.55)`,
                  boxShadow: `0 0 ${size * 4}px rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.35)`,
                  transition: 'background 0.8s ease, box-shadow 0.8s ease',
                }}
                animate={{
                  y: ['0vh', '-110vh'],
                  x: [0, drift, -drift, 0],
                  opacity: [0, 0.85, 0.85, 0],
                }}
                transition={{
                  duration,
                  delay,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
            );
          })}
        </div>

        {/* Animated film grain overlay — removed in favor of parallax depth */}

        {/* ─── PARALLAX DEPTH LAYERS ─────────────────────────
            Single layer — slow drifting diamond watermarks. */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ y: bgFarY, x: bgFarX, opacity: 0.55 }}
        >
          <ParallaxFarLayer activeColor={mechanics[activeIdx]?.color || '#dcc878'} colorRGB={mechanics[activeIdx]?.colorRGB || '220,200,120'} />
        </motion.div>

        {/* Ornamental corner sigils — gilded brackets */}
        <CornerSigils color={mechanics[activeIdx]?.color || '#dcc878'} colorRGB={mechanics[activeIdx]?.colorRGB || '220,200,120'} />

        {/* Side rails — vertical lines flanking the card */}
        <motion.div
          className="absolute top-1/2 left-[8vw] -translate-y-1/2 w-px hidden md:block pointer-events-none"
          style={{
            height: '40vh',
            background: `linear-gradient(to bottom, transparent, rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.32), transparent)`,
            transition: 'background 0.8s ease',
          }}
        />
        <motion.div
          className="absolute top-1/2 right-[8vw] -translate-y-1/2 w-px hidden md:block pointer-events-none"
          style={{
            height: '40vh',
            background: `linear-gradient(to bottom, transparent, rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.32), transparent)`,
            transition: 'background 0.8s ease',
          }}
        />

        {/* Top header strip — progress bar + counter */}
        <div className="absolute top-0 left-0 right-0 z-20 px-4 sm:px-10 pt-5 sm:pt-8 pointer-events-none">
          <div className="max-w-5xl mx-auto flex items-end justify-between mb-3 sm:mb-4">
            <span
              className="font-mono uppercase text-[9px] sm:text-[0.62rem]"
              style={{
                letterSpacing: '0.32em',
                color: 'rgba(220,200,120,0.7)',
              }}
            >
              Codex of Decrees
            </span>
            <span
              className="font-mono uppercase text-[10px] sm:text-[0.78rem]"
              style={{
                fontFamily: "'Cinzel', serif",
                letterSpacing: '0.16em',
                color: '#e6dcc8',
              }}
            >
              <span style={{ color: mechanics[activeIdx]?.color || '#dcc878' }}>
                {mechanics[activeIdx]?.numeral}
              </span>
              <span style={{ color: 'rgba(220,200,120,0.4)', margin: '0 8px' }}>·</span>
              <span style={{ color: 'rgba(230,220,200,0.6)' }}>{N === 6 ? 'VI' : N}</span>
            </span>
          </div>
          {/* Progress bar */}
          <div
            className="max-w-5xl mx-auto h-px relative"
            style={{ background: 'rgba(220,200,120,0.14)' }}
          >
            <motion.div
              className="absolute top-0 left-0 h-full"
              style={{
                width: progressWidth,
                background: `linear-gradient(90deg, transparent, ${mechanics[activeIdx]?.color || '#dcc878'}, ${mechanics[activeIdx]?.color || '#dcc878'})`,
                boxShadow: `0 0 12px rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.55)`,
              }}
            />
          </div>
        </div>

        {/* Sliding card track */}
        <motion.div
          ref={trackRef}
          className="absolute inset-y-0 left-0 flex items-center"
          style={{ x, width: `${N * 100}vw` }}
        >
          {mechanics.map((m, i) => (
            <div
              key={m.id}
              className="flex-shrink-0 h-full flex items-center justify-center"
              style={{ width: '100vw' }}
            >
              <MechanicCarouselCard mechanic={m} index={i} total={N} active={i === activeIdx} />
            </div>
          ))}
        </motion.div>

        {/* Bottom — dots + scroll hint */}
        <div className="absolute bottom-0 left-0 right-0 z-20 px-4 sm:px-10 pb-20 sm:pb-8 pointer-events-none">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              {mechanics.map((m, i) => (
                <span
                  key={m.id}
                  className="inline-block rounded-full transition-all duration-500"
                  style={{
                    width: i === activeIdx ? 28 : 6,
                    height: 6,
                    background: i === activeIdx ? m.color : 'rgba(220,200,120,0.22)',
                    boxShadow: i === activeIdx ? `0 0 10px rgba(${m.colorRGB},0.6)` : 'none',
                  }}
                />
              ))}
            </div>
            {activeIdx < N - 1 && (
              <motion.span
                className="font-mono uppercase inline-flex items-center justify-center sm:justify-end gap-2 text-[11px] sm:text-[0.78rem]"
                style={{
                  letterSpacing: '0.28em',
                  color: '#e8d9a8',
                  textShadow: '0 0 10px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)',
                }}
                animate={{ opacity: [0.75, 1, 0.75], y: [0, 1.5, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              >
                <span>scroll to continue</span>
                <ChevronRight className="w-3.5 h-3.5 rotate-90" style={{ strokeWidth: 2.5 }} />
              </motion.span>
            )}
          </div>

          {/* Latin decree — fades per active card */}
          <div className="max-w-5xl mx-auto mt-3 sm:mt-5 flex items-center justify-center pointer-events-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={mechanics[activeIdx]?.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2 sm:gap-3 max-w-full px-2"
              >
                <span
                  className="h-px w-5 sm:w-8 flex-shrink-0"
                  style={{ background: `linear-gradient(90deg, transparent, rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.45))` }}
                />
                <span
                  className="text-[10px] sm:text-[0.78rem] text-center"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    fontStyle: 'italic',
                    letterSpacing: '0.14em',
                    color: `rgba(${mechanics[activeIdx]?.colorRGB || '230,220,200'},0.78)`,
                    textShadow: `0 0 12px rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.35)`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mechanics[activeIdx]?.decree}
                </span>
                <span
                  className="h-px w-5 sm:w-8 flex-shrink-0"
                  style={{ background: `linear-gradient(270deg, transparent, rgba(${mechanics[activeIdx]?.colorRGB || '220,200,120'},0.45))` }}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Ornamental Corner Sigils ─────────────────────────────
   Four gilded brackets anchored to the corners of the pinned
   viewport. Color shifts with the active card. */
const CornerSigils = ({ color, colorRGB }) => {
  const stroke = `rgba(${colorRGB},0.45)`;
  const glow = `drop-shadow(0 0 8px rgba(${colorRGB},0.35))`;
  const Bracket = ({ className, transform }) => (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      className={`absolute pointer-events-none transition-all duration-700 ${className}`}
      style={{ filter: glow, transform }}
    >
      {/* Outer L */}
      <path
        d="M2 14 V2 H14"
        fill="none"
        stroke={stroke}
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Inner accent */}
      <path
        d="M8 22 V8 H22"
        fill="none"
        stroke={stroke}
        strokeWidth="0.6"
        strokeLinecap="round"
        opacity="0.5"
      />
      {/* Tiny gem dot */}
      <circle cx="2" cy="2" r="1.5" fill={color} opacity="0.9" />
    </svg>
  );
  return (
    <>
      <Bracket className="top-6 left-6" transform="none" />
      <Bracket className="top-6 right-6" transform="scaleX(-1)" />
      <Bracket className="bottom-6 left-6" transform="scaleY(-1)" />
      <Bracket className="bottom-6 right-6" transform="scale(-1,-1)" />
    </>
  );
};

/* ─── Parallax FAR Layer ────────────────────────────────────
   Slow-drifting diamond watermarks — top & bottom of viewport. */
const ParallaxFarLayer = ({ activeColor, colorRGB }) => {
  const stroke = `rgba(${colorRGB},0.22)`;
  return (
    <div className="absolute inset-0">
      {/* Huge faint diamond watermark, top */}
      <svg
        className="absolute top-[8%] left-1/2 -translate-x-1/2"
        width="340"
        height="340"
        viewBox="0 0 100 100"
        style={{ opacity: 0.32, filter: `drop-shadow(0 0 18px rgba(${colorRGB},0.18))` }}
      >
        <polygon points="50,5 95,50 50,95 5,50" fill="none" stroke={stroke} strokeWidth="0.5" />
        <polygon points="50,18 82,50 50,82 18,50" fill="none" stroke={stroke} strokeWidth="0.4" opacity="0.7" />
        <polygon points="50,30 70,50 50,70 30,50" fill="none" stroke={stroke} strokeWidth="0.3" opacity="0.5" />
        <circle cx="50" cy="50" r="1.5" fill={activeColor} opacity="0.8" />
      </svg>
      {/* Mirror at bottom */}
      <svg
        className="absolute bottom-[6%] left-1/2 -translate-x-1/2"
        width="280"
        height="280"
        viewBox="0 0 100 100"
        style={{ opacity: 0.26, filter: `drop-shadow(0 0 18px rgba(${colorRGB},0.18))` }}
      >
        <polygon points="50,5 95,50 50,95 5,50" fill="none" stroke={stroke} strokeWidth="0.5" />
        <polygon points="50,22 78,50 50,78 22,50" fill="none" stroke={stroke} strokeWidth="0.4" opacity="0.6" />
      </svg>
    </div>
  );
};

/* ─── Single full-screen carousel card ────────────────────── */
const MechanicCarouselCard = ({ mechanic: m, index, total, active }) => {
  const Icon = m.icon;
  const accentSoft = `rgba(${m.colorRGB},0.55)`;
  const accentMid = `rgba(${m.colorRGB},0.28)`;
  const accentDim = `rgba(${m.colorRGB},0.18)`;

  return (
    <motion.div
      className="relative w-full max-w-3xl mx-auto px-4 sm:px-10"
      animate={active ? { opacity: 1, scale: 1 } : { opacity: 0.35, scale: 0.96 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div
        className="relative px-5 sm:px-10 lg:px-14 py-7 sm:py-14"
        style={{
          background:
            'linear-gradient(180deg, rgba(20,8,12,0.85) 0%, rgba(8,4,8,0.96) 100%)',
          border: `1px solid ${active ? accentSoft : 'rgba(220,200,120,0.18)'}`,
          borderRadius: 4,
          boxShadow: active
            ? `0 0 60px rgba(${m.colorRGB},0.22), 0 0 120px rgba(${m.colorRGB},0.12), inset 0 0 32px rgba(0,0,0,0.55)`
            : '0 0 32px rgba(0,0,0,0.55), inset 0 0 28px rgba(0,0,0,0.5)',
          transition: 'border-color 0.5s, box-shadow 0.5s',
          overflow: 'hidden',
        }}
      >
        {/* Massive watermark numeral behind the content */}
        <div
          className="absolute right-2 sm:right-6 top-1 sm:top-4 pointer-events-none select-none"
          style={{
            fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
            fontSize: 'clamp(8rem, 20vw, 18rem)',
            fontWeight: 700,
            color: `rgba(${m.colorRGB},0.06)`,
            letterSpacing: '0.04em',
            lineHeight: 0.85,
            textShadow: `0 0 40px rgba(${m.colorRGB},0.08)`,
            zIndex: 0,
          }}
        >
          {m.numeral}
        </div>

        {/* Gold corner brackets — larger for the carousel */}
        <div
          className="absolute top-2 left-2 z-30"
          style={{
            width: 22, height: 22,
            borderTop: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
            borderLeft: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
          }}
        />
        <div
          className="absolute top-2 right-2 z-30"
          style={{
            width: 22, height: 22,
            borderTop: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
            borderRight: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
          }}
        />
        <div
          className="absolute bottom-2 left-2 z-30"
          style={{
            width: 22, height: 22,
            borderBottom: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
            borderLeft: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
          }}
        />
        <div
          className="absolute bottom-2 right-2 z-30"
          style={{
            width: 22, height: 22,
            borderBottom: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
            borderRight: `1.5px solid ${active ? accentSoft : 'rgba(220,200,120,0.5)'}`,
          }}
        />

        {/* Subtle radial accent */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 30% 110%, rgba(${m.colorRGB},0.18), transparent 55%)`,
          }}
        />

        {/* Header — tag + numeral position */}
        <div className="relative z-10 flex items-center justify-between mb-4 sm:mb-6">
          <span
            className="font-mono uppercase text-[10px] sm:text-[0.62rem]"
            style={{
              letterSpacing: '0.28em',
              color: m.color,
              padding: '5px 9px',
              border: `1px solid rgba(${m.colorRGB},0.32)`,
              background: `rgba(${m.colorRGB},0.08)`,
              borderRadius: 2,
            }}
          >
            {m.tag}
          </span>
          <span
            className="font-mono uppercase text-[9px] sm:text-[0.58rem]"
            style={{
              letterSpacing: '0.28em',
              color: 'rgba(220,200,120,0.5)',
            }}
          >
            Decree {m.numeral} · of · {total === 6 ? 'VI' : total}
          </span>
        </div>

        {/* Icon + title */}
        <div className="relative z-10 flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
          <motion.div
            animate={active ? { scale: [1, 1.08, 1] } : { scale: 1 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            className="relative inline-flex items-center justify-center flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(${m.colorRGB},0.22) 0%, transparent 70%)`,
            }}
          >
            <Icon
              className="w-6 h-6 sm:w-7 sm:h-7"
              style={{
                color: m.color,
                filter: `drop-shadow(0 0 10px rgba(${m.colorRGB},0.6))`,
              }}
            />
          </motion.div>
          <h3
            style={{
              fontFamily: "'Cinzel', 'Cinzel Decorative', serif",
              fontSize: 'clamp(1.35rem, 3vw, 2.2rem)',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: '#e6dcc8',
              textShadow: active ? `0 0 14px rgba(${m.colorRGB},0.4)` : 'none',
              transition: 'text-shadow 0.5s',
              lineHeight: 1.1,
            }}
          >
            {m.title}
          </h3>
        </div>

        {/* Hook */}
        <p
          className="italic relative z-10 mb-4 sm:mb-5"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: 'clamp(0.95rem, 1.6vw, 1.4rem)',
            color: `rgba(${m.colorRGB},0.78)`,
            lineHeight: 1.4,
          }}
        >
          {m.hook}
        </p>

        {/* Divider rune */}
        <div
          className="relative z-10 mb-4 sm:mb-5"
          style={{
            height: 1,
            background: `linear-gradient(90deg, ${accentMid}, transparent 80%)`,
          }}
        />

        {/* Body */}
        <p
          className="relative z-10"
          style={{
            fontSize: 'clamp(0.82rem, 1.2vw, 1.05rem)',
            color: 'rgba(230,220,200,0.62)',
            lineHeight: 1.55,
            maxWidth: '60ch',
          }}
        >
          {m.detail}
        </p>
      </div>
    </motion.div>
  );
};

/* (Legacy mechanic-grid placeholder — kept to satisfy the closing tags from the old block) */

// Season Live Widget — The main hook
const SeasonWidget = () => {
  const navigate = useNavigate();
  const { isDemo } = useDemo();
  const { isLive } = useAppMode();
  const [season, setSeason] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSeason = async () => {
      // ─── LIVE MODE: read from on-chain devnet program ───
      if (isLive) {
        try {
          const { readSeason, fromRaw } = await import('../lib/solana/devnet');
          const onSeason = await readSeason();
          if (cancelled) return;
          if (!onSeason) { setSeason(null); return; }
          const burned = fromRaw(onSeason.total_burned);
          const target = fromRaw(onSeason.burn_milestone);
          const pct = target > 0 ? (burned / target) * 100 : 0;
          const prizePool = fromRaw(onSeason.next_reward_pool);
          const end_date = onSeason.end_time ? new Date(onSeason.end_time * 1000).toISOString() : null;
          setSeason({
            active: !onSeason.settled,
            season_number: onSeason.season_number,
            name: `Season ${onSeason.season_number} · Devnet`,
            total_burned: burned,
            burn_target: target,
            current_milestone_pct: pct,
            // UI bar reads `burn_progress` — alias on-chain percentage.
            burn_progress: Math.round(pct),
            participants: 0,
            // UI reads `prize_pool` — keep both keys for compatibility.
            prize_pool: prizePool,
            rewards_pool: prizePool,
            end_date,
            is_live: true,
            paused: onSeason.paused,
          });
        } catch { if (!cancelled) setSeason(null); }
        return;
      }
      try {
        const url = `${API_URL}/api/seasons/progress${isDemo ? '?include_demo=true' : ''}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.active) {
          // Hide demo seasons from non-demo visitors — show "coming soon" instead
          if (data.is_demo && !isDemo) {
            setSeason(null);
          } else {
            setSeason(data);
          }
        }
      } catch {}
    };
    fetchSeason();
    const interval = setInterval(fetchSeason, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isDemo, isLive]);

  const getTimeLeft = () => {
    if (!season?.end_date) return '';
    const diff = new Date(season.end_date) - new Date();
    if (diff <= 0) return 'ENDED';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    return `${d}d ${h}h remaining`;
  };

  return (
    <section className="py-24 md:py-40 px-4 sm:px-6 relative overflow-hidden">
      {/* Cinematic lava forge backdrop — soft, masked to fade at edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${lavaBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.32,
          mixBlendMode: 'screen',
          maskImage: 'radial-gradient(ellipse 65% 55% at 50% 50%, black 0%, black 35%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 55% at 50% 50%, black 0%, black 35%, transparent 85%)',
        }}
      />
      {/* Deep lava ambient — soft radial only, no rectangular bands */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(120,25,0,0.14) 0%, rgba(60,15,0,0.05) 40%, transparent 75%)' }} />
      <div className="hidden md:block absolute top-1/2 left-1/3 w-[700px] h-[700px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2" style={{ background: 'radial-gradient(circle, rgba(255,107,0,0.14) 0%, transparent 65%)', filter: 'blur(140px)' }} />
      <div className="hidden md:block absolute bottom-1/4 right-1/4 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(220,30,0,0.1) 0%, transparent 65%)', filter: 'blur(120px)' }} />

      {/* Rising embers - desktop only */}
      <div className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 14 }).map((_, i) => (
          <motion.div key={i}
            className="absolute rounded-full"
            style={{
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              left: `${(i * 7.3) % 100}%`,
              bottom: '-20px',
              background: i % 3 === 0 ? '#ff9500' : i % 3 === 1 ? '#ff4400' : '#ffd700',
              boxShadow: `0 0 8px ${i % 3 === 0 ? '#ff9500' : i % 3 === 1 ? '#ff4400' : '#ffd700'}`,
            }}
            animate={{ y: [-20, -600], opacity: [0, 0.9, 0], x: [0, (i % 2 === 0 ? 30 : -30)] }}
            transition={{ duration: 6 + (i % 4), repeat: Infinity, delay: i * 0.5, ease: 'easeOut' }}
          />
        ))}
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* ═══ HERO HEADER ═══ */}
        <Reveal>
          <div className="text-center mb-6 md:mb-8">
            {/* Pulsing season badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 relative"
              animate={{ boxShadow: ['0 0 16px rgba(255,107,0,0.15)', '0 0 32px rgba(255,107,0,0.35)', '0 0 16px rgba(255,107,0,0.15)'] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
              style={{ background: 'linear-gradient(135deg, rgba(255,68,0,0.1), rgba(255,107,0,0.15))', border: '1px solid rgba(255,107,0,0.35)' }}
            >
              <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                <Flame className="w-3.5 h-3.5" style={{ color: '#ff9500', filter: 'drop-shadow(0 0 6px #ff6b00)' }} />
              </motion.div>
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: isLive ? '#a8def7' : '#ffb570', textShadow: isLive ? '0 0 8px rgba(125,208,255,0.5)' : '0 0 8px rgba(255,107,0,0.5)' }}>
                {season ? (
                  isLive
                    ? `Season ${season.season_number} · Devnet · Live`
                    : `Season ${season.season_number || 'III'} · Active`
                ) : 'Season · Coming Soon'}
              </span>
              {season && (
                <motion.span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#ff4400', boxShadow: '0 0 8px #ff4400' }}
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              )}
            </motion.div>

            {/* Title — single line, compact */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-2 leading-tight tracking-tight"
              style={{
                fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                background: 'linear-gradient(180deg, #ffffff 0%, #ffd700 40%, #ff9500 70%, #ff4400 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 18px rgba(255,107,0,0.35))',
              }}
              data-testid="season-highlight-title"
            >
              {season
                ? (season.season_name || season.name || 'The Awakening')
                : 'The Next Descent'}
            </h2>

            {/* Flourish divider */}
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="h-[1px] w-10 md:w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.5))' }} />
              <div className="w-1 h-1 rotate-45" style={{ background: '#ff6b00', boxShadow: '0 0 6px #ff6b00' }} />
              <div className="h-[1px] w-10 md:w-16" style={{ background: 'linear-gradient(270deg, transparent, rgba(255,107,0,0.5))' }} />
            </div>

            <p className="text-xs md:text-sm max-w-md mx-auto px-2" style={{ color: 'rgba(255,220,180,0.6)', fontFamily: "'Cinzel', serif" }}>
              {season
                ? 'Burn together. Hit the target. Split the rewards.'
                : 'Every 30 days, a new challenge. Burn, compete, claim your share.'}
            </p>
          </div>
        </Reveal>

        {season ? (
          <Reveal delay={0.15}>
            <div className="relative">
              {/* Outer glow halo */}
              <motion.div
                className="absolute -inset-2 md:-inset-4 rounded-2xl pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, rgba(255,107,0,0.15), transparent 70%)', filter: 'blur(24px)' }}
                animate={{ opacity: [0.45, 0.85, 0.45] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Main war-room panel */}
              <div className="relative rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(20,6,2,0.92) 0%, rgba(10,2,0,0.96) 50%, rgba(25,8,0,0.92) 100%)',
                  border: '1px solid rgba(255,107,0,0.3)',
                  boxShadow: '0 0 60px rgba(255,68,0,0.18), inset 0 0 60px rgba(255,107,0,0.04)',
                }}
              >
                {/* Corner brackets */}
                {[
                  { pos: 'top-3 left-3', b: 'border-t-2 border-l-2' },
                  { pos: 'top-3 right-3', b: 'border-t-2 border-r-2' },
                  { pos: 'bottom-3 left-3', b: 'border-b-2 border-l-2' },
                  { pos: 'bottom-3 right-3', b: 'border-b-2 border-r-2' },
                ].map((c, i) => (
                  <div key={i} className={`absolute ${c.pos} w-4 h-4 ${c.b} pointer-events-none`} style={{ borderColor: 'rgba(255,149,0,0.5)' }} />
                ))}
                {/* Top accent strip */}
                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #ff4400 30%, #ff9500 50%, #ff4400 70%, transparent 100%)' }} />

                <div className="p-4 md:p-6">
                  {/* ── COMPACT PROGRESS HERO ── */}
                  <div className="mb-5">
                    <div className="flex items-end justify-between flex-wrap gap-2 mb-2">
                      <div>
                        <p className="text-[9px] md:text-[10px] tracking-[0.25em] uppercase font-bold mb-0.5" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,149,0,0.6)' }}>Community Burn Gauge</p>
                        <p className="text-[10px] md:text-[11px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          <span style={{ color: '#ff9500' }}>{(season.total_burned || 0).toLocaleString()}</span>
                          <span className="opacity-50"> / {(season.burn_target || 0).toLocaleString()} $GFG</span>
                        </p>
                      </div>
                      <motion.div
                        className="font-black leading-none"
                        style={{
                          fontFamily: "'Impact', 'Cinzel Decorative', sans-serif",
                          fontSize: 'clamp(2rem, 6vw, 3.25rem)',
                          background: 'linear-gradient(180deg, #ffd700 0%, #ff9500 50%, #ff4400 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          filter: 'drop-shadow(0 0 18px rgba(255,107,0,0.5))',
                          letterSpacing: '-0.02em',
                        }}
                        animate={{ textShadow: ['0 0 20px rgba(255,107,0,0.4)', '0 0 40px rgba(255,107,0,0.7)', '0 0 20px rgba(255,107,0,0.4)'] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                      >
                        {season.burn_progress}<span className="text-lg md:text-2xl opacity-70">%</span>
                      </motion.div>
                    </div>

                    {/* Fire-lava progress bar - compact */}
                    <div className="relative h-6 md:h-7 rounded-md overflow-hidden"
                      style={{ background: 'linear-gradient(180deg, rgba(40,10,0,0.9), rgba(20,5,0,0.95))', border: '1px solid rgba(255,107,0,0.25)', boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6)' }}
                    >
                      {/* Grid pattern */}
                      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent 0, transparent 9%, rgba(255,107,0,0.15) 9%, rgba(255,107,0,0.15) 10%)' }} />

                      {/* Animated fire fill */}
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, season.burn_progress)}%` }}
                        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
                        className="h-full relative overflow-hidden"
                        style={{
                          background: 'linear-gradient(90deg, #dc1f00 0%, #ff4400 35%, #ff9500 70%, #ffd700 100%)',
                          boxShadow: '0 0 20px rgba(255,107,0,0.6), inset 0 0 10px rgba(255,230,180,0.4)',
                        }}
                      >
                        <motion.div
                          className="absolute top-0 bottom-0 w-20"
                          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }}
                          animate={{ x: ['-80px', '500px'] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                        />
                      </motion.div>

                      {/* Leading flame tip */}
                      {season.burn_progress > 0 && (
                        <motion.div
                          className="absolute top-0 bottom-0 w-1 pointer-events-none"
                          style={{ left: `calc(${Math.min(100, season.burn_progress)}% - 2px)`, background: 'linear-gradient(180deg, #ffffff, #ffd700, #ff6b00)', boxShadow: '0 0 16px 3px rgba(255,230,140,0.9), 0 0 30px 6px rgba(255,107,0,0.5)' }}
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 0.8, repeat: Infinity }}
                        />
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 mt-1.5 justify-end">
                      <div className="w-1 h-1 rounded-full" style={{ background: '#ff6b00', boxShadow: '0 0 4px #ff6b00' }} />
                      <span className="text-[9px] md:text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        {Math.max(0, (season.burn_target || 0) - (season.total_burned || 0)).toLocaleString()} $GFG to target
                      </span>
                    </div>
                  </div>

                  {/* ── COMPACT STATS ROW ── */}
                  <div className="grid grid-cols-3 gap-2 md:gap-3 mb-5">
                    {[
                      { icon: Trophy, label: 'Rewards', value: (season.prize_pool || 0).toLocaleString(), unit: '$GFG', c: '#ffd700' },
                      { icon: Users, label: 'Burners', value: (season.participants || 0).toLocaleString(), unit: 'active', c: '#a855f7' },
                      { icon: Timer, label: 'Time Left', value: getTimeLeft().replace(' remaining', '') || '—', unit: 'until close', c: '#ff6b00' },
                    ].map((stat, i) => (
                      <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.08 + i * 0.08 }}
                        whileHover={{ y: -2 }}
                        className="relative p-2.5 md:p-3 rounded-lg min-w-0"
                        style={{
                          background: `linear-gradient(135deg, ${stat.c}08 0%, rgba(0,0,0,0.4) 50%, ${stat.c}05 100%)`,
                          border: `1px solid ${stat.c}25`,
                          boxShadow: `inset 0 0 12px ${stat.c}06`,
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="relative flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0"
                            style={{ background: `radial-gradient(circle, ${stat.c}30 0%, rgba(0,0,0,0.5) 75%)`, border: `1px solid ${stat.c}50` }}>
                            <stat.icon className="w-3 h-3" style={{ color: stat.c, filter: `drop-shadow(0 0 4px ${stat.c})` }} />
                          </div>
                          <span className="text-[8px] md:text-[9px] tracking-[0.15em] uppercase font-bold truncate" style={{ fontFamily: "'Courier New', monospace", color: `${stat.c}aa` }}>{stat.label}</span>
                        </div>
                        <p className="font-black leading-none mb-0.5 truncate"
                          style={{
                            fontFamily: "'Impact', sans-serif",
                            fontSize: 'clamp(0.95rem, 3vw, 1.4rem)',
                            color: stat.c,
                            textShadow: `0 0 12px ${stat.c}60`,
                            letterSpacing: '-0.01em',
                          }}>
                          {stat.value}
                        </p>
                        <p className="text-[8px] md:text-[9px] font-mono truncate" style={{ color: 'rgba(255,255,255,0.3)' }}>{stat.unit}</p>
                        <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{ background: `linear-gradient(90deg, transparent, ${stat.c}, transparent)`, opacity: 0.4 }} />
                      </motion.div>
                    ))}
                  </div>

                  {/* ── CTA ── */}
                  <div className="flex justify-center">
                    <motion.button
                      onClick={() => navigate('/seasons')}
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      data-testid="join-season-btn"
                      className="group relative inline-flex items-center gap-2 px-6 md:px-10 py-2.5 md:py-3 overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, rgba(220,30,0,0.3), rgba(255,107,0,0.25))',
                        border: '1px solid rgba(255,149,0,0.6)',
                        boxShadow: '0 0 24px rgba(255,107,0,0.35), inset 0 0 12px rgba(255,200,100,0.08)',
                        clipPath: 'polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%)',
                      }}
                    >
                      <motion.span
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,215,120,0.2), transparent)' }}
                      />
                      <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.4, repeat: Infinity }}>
                        <Flame className="w-4 h-4 md:w-5 md:h-5 relative z-10" style={{ color: '#ffd700', filter: 'drop-shadow(0 0 8px #ff6b00)' }} />
                      </motion.div>
                      <span className="relative z-10 text-xs md:text-sm font-black tracking-[0.25em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: '#fff5d6', textShadow: '0 0 10px rgba(255,107,0,0.6)' }}>
                        Join The Burn
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 relative z-10 group-hover:translate-x-1 transition-transform" style={{ color: '#ffd700' }} />
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ) : (
          // ═══ COMING SOON STATE ═══
          <Reveal delay={0.15}>
            <div className="relative">
              <motion.div
                className="absolute -inset-3 rounded-2xl pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, rgba(168,85,247,0.15), transparent 70%)', filter: 'blur(20px)' }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3.5, repeat: Infinity }}
              />
              <div className="relative rounded-2xl overflow-hidden p-6 md:p-10"
                style={{
                  background: 'linear-gradient(135deg, rgba(15,5,25,0.92) 0%, rgba(8,2,15,0.96) 50%, rgba(20,8,30,0.92) 100%)',
                  border: '1px solid rgba(168,85,247,0.3)',
                  boxShadow: '0 0 60px rgba(168,85,247,0.12), inset 0 0 60px rgba(168,85,247,0.04)',
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #a855f7, transparent)' }} />
                {[
                  { pos: 'top-3 left-3', b: 'border-t-2 border-l-2' },
                  { pos: 'top-3 right-3', b: 'border-t-2 border-r-2' },
                  { pos: 'bottom-3 left-3', b: 'border-b-2 border-l-2' },
                  { pos: 'bottom-3 right-3', b: 'border-b-2 border-r-2' },
                ].map((c, i) => (
                  <div key={i} className={`absolute ${c.pos} w-4 h-4 ${c.b} pointer-events-none`} style={{ borderColor: 'rgba(168,85,247,0.5)' }} />
                ))}

                <div className="grid sm:grid-cols-3 gap-4 md:gap-5">
                  {[
                    { icon: Gamepad2, title: 'Play Free', desc: 'Arcade games use in-game credits. No purchase needed to earn Season Points.', color: '#a855f7' },
                    { icon: Flame, title: 'Burn to Compete', desc: 'Voluntary token burns through the Burn Portal earn bonus Season Points.', color: '#ff6b00' },
                    { icon: Trophy, title: 'Claim Rewards', desc: 'Community rewards distribute to contributors when the burn target is met.', color: '#ffd700' },
                  ].map((item, i) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 + i * 0.12 }}
                      whileHover={{ y: -3 }}
                      className="relative p-5 rounded-xl"
                      style={{
                        background: `linear-gradient(135deg, ${item.color}08 0%, rgba(0,0,0,0.4) 100%)`,
                        border: `1px solid ${item.color}25`,
                      }}
                    >
                      <div className="relative flex items-center justify-center w-11 h-11 rounded-full mb-3"
                        style={{ background: `radial-gradient(circle, ${item.color}25 0%, rgba(0,0,0,0.5) 75%)`, border: `1px solid ${item.color}50` }}>
                        <item.icon className="w-5 h-5" style={{ color: item.color, filter: `drop-shadow(0 0 6px ${item.color})` }} />
                      </div>
                      <h3 className="text-base md:text-lg font-bold mb-1.5" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(240,230,255,0.9)' }}>{item.title}</h3>
                      <p className="text-[12px] md:text-[13px] leading-relaxed" style={{ color: 'rgba(220,210,240,0.55)' }}>{item.desc}</p>
                      <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{ background: `linear-gradient(90deg, transparent, ${item.color}, transparent)`, opacity: 0.4 }} />
                    </motion.div>
                  ))}
                </div>

                <div className="text-center mt-7 md:mt-9">
                  <motion.button
                    onClick={() => navigate('/seasons')}
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="preview-seasons-btn"
                    className="inline-flex items-center gap-2.5 px-7 py-3.5"
                    style={{
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(124,30,150,0.2))',
                      border: '1px solid rgba(168,85,247,0.45)',
                      boxShadow: '0 0 24px rgba(168,85,247,0.25)',
                      clipPath: 'polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%)',
                    }}
                  >
                    <BookOpen className="w-4 h-4" style={{ color: '#c084fc' }} />
                    <span className="text-sm font-bold tracking-[0.2em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: '#e9d5ff' }}>Preview the Seasons</span>
                    <ArrowRight className="w-4 h-4" style={{ color: '#c084fc' }} />
                  </motion.button>
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

// CTA Section
const CTA = () => {
  const navigate = useNavigate();
  const { connected, publicKey } = useWallet();
  const { features } = useFeatureFlags();
  const { isDemo, enterDemo } = useDemo();
  const hideWallet = !features.wallet_connect;
  const isAdmin = connected && publicKey?.toBase58() === ADMIN_WALLET;

  return (
    <section className="py-16 md:py-32 px-4 sm:px-6 bg-gradient-to-b from-black to-purple-900/20">
      <div className="max-w-3xl mx-auto text-center">
        <Reveal direction="scale">
          <img 
            src={GREMLIN_LOGO}
            alt="Gas Free Gremlins"
            loading="lazy"
            decoding="async"
            className="w-20 h-20 md:w-24 md:h-24 mx-auto object-contain mb-6 md:mb-8 opacity-80"
          />
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 md:mb-6">
            Descend Into the <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">Sewers</span>
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="text-gray-400 text-lg mb-10">
            The Gremlins have waited long enough. Enter the demo and claim your place in the ledger.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="relative">
            {/* Enter Heist button - shown when connected OR in demo */}
            <button
              onClick={() => (connected || isDemo) && navigate('/arcade')}
              className={`group relative overflow-hidden hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 ${(connected || isDemo) ? 'opacity-100 visible' : 'opacity-0 invisible absolute'}`}
            >
              {/* Outer glow */}
              <div className="absolute inset-0 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'radial-gradient(ellipse, rgba(255,140,0,0.2), transparent 70%)' }} />
              
              {/* Main button */}
              <div className="relative flex items-center gap-3 px-10 py-4 backdrop-blur-sm rounded-xl transition-all duration-300"
                style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,140,0,0.35)' }}>
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: 'linear-gradient(90deg, rgba(255,140,0,0.04), transparent, rgba(255,140,0,0.04))' }} />
                <Gamepad2 className="w-5 h-5 relative" style={{ color: '#ff8c00' }} />
                <span className="relative text-base font-bold tracking-wider uppercase" style={{ fontFamily: "'Cinzel', serif", background: 'linear-gradient(90deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Descend Into The Heist
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-all" style={{ color: '#ff8c00' }} />
              </div>
            </button>
            
            {/* Wallet + Demo buttons — shown when not connected and not in demo, OR for admin to preview demo */}
            {((!connected && !isDemo) || (isAdmin && !isDemo)) && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-300">
              {!hideWallet && !isAdmin && (
                <>
                  <WalletMultiButton className="!bg-gradient-to-r !from-purple-600 !to-pink-600 !rounded-full !px-10 !py-4 !font-bold !text-lg" />
                  <span className="text-xs uppercase tracking-[0.3em] text-gray-500" style={{ fontFamily: "'Courier New', monospace" }}>or</span>
                </>
              )}
              <motion.button
                onClick={enterDemo}
                data-testid="cta-enter-demo-btn"
                className="group relative overflow-hidden rounded-xl hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
                whileHover={{ boxShadow: '0 0 40px rgba(168,85,247,0.3), 0 0 80px rgba(57,255,20,0.1)' }}
              >
                <motion.div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background: 'conic-gradient(from 0deg, #a855f7, #39ff14, #c084fc, #39ff14, #a855f7)',
                    padding: '1.5px',
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                >
                  <div className="w-full h-full rounded-xl" style={{ background: '#0a0015' }} />
                </motion.div>
                <div className="absolute inset-[2px] rounded-[10px] overflow-hidden">
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(168,85,247,0.12), transparent 70%)' }} />
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(57,255,20,0.06), rgba(168,85,247,0.08), transparent)',
                      backgroundSize: '200% 100%',
                    }}
                    animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
                <div className="relative z-10 flex items-center gap-3 px-8 py-3.5">
                  <motion.div
                    animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Eye className="w-5 h-5" style={{ color: '#c084fc', filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.7))' }} />
                  </motion.div>
                  <span
                    className="text-sm tracking-[0.15em] uppercase font-bold"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      background: 'linear-gradient(90deg, #c084fc, #e9d5ff, #39ff14)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.4))',
                    }}
                  >
                    Enter Demo
                  </span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: '#39ff14', filter: 'drop-shadow(0 0 4px rgba(57,255,20,0.5))' }} />
                </div>
              </motion.button>
            </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═══ GENESIS SHRINE — Public altar honoring pre-launch Genesis Testers ═══
const GenesisShrine = () => {
  const { isDemo } = useDemo();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = () => {
      fetch(`${API_URL}/api/genesis/public-stats`)
        .then(r => r.json()).then(setStats).catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 20000);
    return () => clearInterval(interval);
  }, []);

  if (!stats) return null;

  return (
    <section
      className="py-24 md:py-40 px-4 sm:px-6 relative overflow-hidden"
      data-testid="genesis-shrine"
    >
      {/* Cinematic shrine backdrop — soft, masked to fade at edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${shrineBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          opacity: 0.45,
          mixBlendMode: 'screen',
          maskImage: 'radial-gradient(ellipse 60% 55% at 50% 50%, black 0%, black 30%, transparent 82%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 55% at 50% 50%, black 0%, black 30%, transparent 82%)',
        }}
      />
      {/* Deep amber ambient — soft radial only, no rectangular bands */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(120,80,0,0.14) 0%, rgba(60,40,0,0.05) 40%, transparent 75%)' }} />
      <div className="hidden md:block absolute top-1/2 left-1/2 w-[620px] h-[620px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2" style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.12) 0%, transparent 65%)', filter: 'blur(130px)' }} />

      {/* Rising gold embers */}
      <div className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 10 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              left: `${(i * 9.5) % 100}%`,
              bottom: '-20px',
              background: i % 2 === 0 ? '#ffd700' : '#fbbf24',
              boxShadow: `0 0 8px ${i % 2 === 0 ? '#ffd700' : '#fbbf24'}`,
            }}
            animate={{ y: [-20, -500], opacity: [0, 0.9, 0], x: [0, (i % 2 === 0 ? 30 : -30)] }}
            transition={{ duration: 7 + (i % 4), repeat: Infinity, delay: i * 0.6, ease: 'easeOut' }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8 md:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4" style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.35)' }}>
            <motion.span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#c084fc', boxShadow: '0 0 6px #c084fc' }}
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            <span className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#c084fc' }}>
              Pre-Launch Only · Permanent Record
            </span>
          </div>

          <h2
            className="text-4xl sm:text-5xl md:text-6xl font-black mb-3 leading-[1.05]"
            style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              background: 'linear-gradient(180deg, #fff5d6 0%, #ffd700 40%, #ff8c00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 22px rgba(251,191,36,0.4))',
            }}
          >
            The Genesis Shrine
          </h2>

          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-[1px] w-12 md:w-20" style={{ background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.5))' }} />
            <Crown className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 6px #fbbf24)' }} />
            <div className="h-[1px] w-12 md:w-20" style={{ background: 'linear-gradient(270deg, transparent, rgba(251,191,36,0.5))' }} />
          </div>

          <p className="text-sm md:text-base max-w-xl mx-auto" style={{ color: 'rgba(255,220,180,0.7)', fontFamily: "'Cinzel', serif" }}>
            Before the token awakens, a select few inscribe their names in the pre-launch ledger.
            They will be remembered on-chain, forever.
          </p>
        </motion.div>

        {/* Counter tableau */}
        <motion.div
          className="relative rounded-xl overflow-hidden p-5 md:p-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: 'linear-gradient(135deg, rgba(20,14,0,0.92) 0%, rgba(10,6,0,0.96) 50%, rgba(25,18,0,0.92) 100%)',
            border: '1px solid rgba(251,191,36,0.3)',
            boxShadow: '0 0 45px rgba(251,191,36,0.12), inset 0 0 40px rgba(251,191,36,0.04)',
          }}
        >
          {/* Corner brackets */}
          {[
            { pos: 'top-2 left-2', b: 'border-t-2 border-l-2' },
            { pos: 'top-2 right-2', b: 'border-t-2 border-r-2' },
            { pos: 'bottom-2 left-2', b: 'border-b-2 border-l-2' },
            { pos: 'bottom-2 right-2', b: 'border-b-2 border-r-2' },
          ].map((c, i) => (
            <div key={i} className={`absolute ${c.pos} w-4 h-4 ${c.b} pointer-events-none`} style={{ borderColor: 'rgba(251,191,36,0.5)' }} />
          ))}
          {/* Top strip */}
          <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }} />

          <div className="grid grid-cols-3 gap-3 md:gap-6">
            {[
              { label: 'Inscribed', value: stats.codes_issued, icon: Crown, color: '#fbbf24', sub: 'Genesis codes issued' },
              { label: 'Awaiting', value: stats.eligible, icon: Flame, color: '#ff8c00', sub: 'Qualify but unclaimed' },
              { label: 'Redeemed', value: stats.codes_claimed, icon: Star, color: '#c084fc', sub: 'Minted at launch' },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                className="relative text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.12 }}
              >
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <s.icon className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: s.color, filter: `drop-shadow(0 0 4px ${s.color})` }} />
                  <span className="text-[8px] md:text-[10px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: `${s.color}cc` }}>
                    {s.label}
                  </span>
                </div>
                <motion.p
                  className="font-black leading-none mb-1"
                  style={{
                    fontFamily: "'Impact', sans-serif",
                    fontSize: 'clamp(1.8rem, 6vw, 3.5rem)',
                    color: s.color,
                    textShadow: `0 0 18px ${s.color}70`,
                    letterSpacing: '-0.02em',
                  }}
                  animate={{ textShadow: [`0 0 12px ${s.color}60`, `0 0 24px ${s.color}90`, `0 0 12px ${s.color}60`] }}
                  transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }}
                >
                  {(s.value || 0).toLocaleString()}
                </motion.p>
                <p className="text-[8px] md:text-[9px] tracking-wider" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,220,180,0.35)' }}>
                  {s.sub}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Recent inscriptions feed */}
          {stats.recent && stats.recent.length > 0 && (
            <div className="mt-6 pt-5 border-t relative" style={{ borderColor: 'rgba(251,191,36,0.15)' }}>
              <p className="text-[9px] md:text-[10px] tracking-[0.3em] uppercase font-bold mb-3 text-center relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(251,191,36,0.65)' }}>
                Recent Inscriptions
              </p>
              <div className="relative max-w-md mx-auto">
                {/* Ancient stone slab backdrop */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `url(${inscriptionSlab})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.55,
                    mixBlendMode: 'screen',
                    maskImage: 'radial-gradient(ellipse 90% 85% at 50% 50%, black 10%, transparent 95%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 90% 85% at 50% 50%, black 10%, transparent 95%)',
                  }}
                />
                <div className="relative z-10 space-y-1.5 p-3">
                  {stats.recent.map((r, i) => (
                    <motion.div
                      key={r.code_prefix + r.issued_at}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }}
                      className="flex items-center justify-between px-3 py-1.5 text-[10px] md:text-xs backdrop-blur-[2px]"
                      style={{ background: 'rgba(8,4,0,0.55)', border: '1px solid rgba(251,191,36,0.22)' }}
                    >
                      <span className="font-mono" style={{ color: 'rgba(255,220,120,0.95)', letterSpacing: '0.1em', textShadow: '0 0 6px rgba(251,191,36,0.4)' }}>
                        {r.code_prefix}
                      </span>
                      <span className="font-mono text-[9px] md:text-[10px]" style={{ color: 'rgba(255,220,120,0.5)' }}>
                        {new Date(r.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Threshold + CTA */}
        <motion.div
          className="text-center mt-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <p className="text-[10px] md:text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,220,180,0.4)' }}>
            Play {stats.threshold_games}+ arcade games · Demo mode · Before token launch
          </p>
          {isDemo ? (
            <motion.button
              onClick={() => navigate('/profile')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              data-testid="genesis-cta-profile"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-xs md:text-sm font-bold tracking-[0.25em] uppercase"
              style={{
                fontFamily: "'Cinzel', serif",
                background: 'linear-gradient(180deg, rgba(251,191,36,0.2), rgba(168,130,0,0.35))',
                border: '1px solid rgba(251,191,36,0.55)',
                color: '#ffd700',
                boxShadow: '0 0 20px rgba(251,191,36,0.2)',
                textShadow: '0 0 8px rgba(251,191,36,0.5)',
              }}
            >
              <Crown className="w-3.5 h-3.5 md:w-4 md:h-4" />
              View My Passport
            </motion.button>
          ) : (
            <p className="text-[10px] md:text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(192,132,252,0.55)' }}>
              Enter Demo mode to earn your inscription
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// ═══ LIVE BURN COUNTER — Monument to Destruction ═══
const BurnCounter = () => {
  const { isDemo, isLive } = useDemo();
  const [stats, setStats] = useState(null);
  const [displayBurned, setDisplayBurned] = useState(0);
  const counterRef = useRef(null);
  const isInView = useInView(counterRef, { once: false, margin: "-50px" });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      // Live mode → read on-chain Season `total_burned` (the only source of truth on devnet).
      if (isLive) {
        try {
          const { readSeason, fromRaw } = await import('../lib/solana/devnet');
          const s = await readSeason();
          if (!cancelled && s) {
            setStats({
              total_burned: fromRaw(s.total_burned),
              live: true,
              season_number: s.season_number,
            });
          }
        } catch { /* ignore — counter stays at last value */ }
        return;
      }
      const url = `${API_URL}/api/burn/global-stats${isDemo ? '?include_demo=true' : ''}`;
      try {
        const r = await fetch(url);
        const d = await r.json();
        if (!cancelled) setStats(d);
      } catch { /* ignore */ }
    };
    tick();
    const interval = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isDemo, isLive]);

  // Animated count-up
  useEffect(() => {
    if (!stats?.total_burned || !isInView) return;
    const target = stats.total_burned;
    const duration = 2000;
    const start = displayBurned;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayBurned(Math.floor(start + (target - start) * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [stats?.total_burned, isInView]);

  const formatNumber = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  return (
    <section ref={counterRef} className="py-16 md:py-28 px-4 sm:px-6 relative overflow-hidden" data-testid="burn-counter-section">
      {/* Deep ambient glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,107,0,0.06) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.15), transparent)' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.08), transparent)' }} />
      </div>

      {/* Floating ember particles */}
      {isInView && [...Array(12)].map((_, i) => (
        <motion.div key={i} className="absolute rounded-full pointer-events-none"
          style={{ width: `${2 + Math.random() * 4}px`, height: `${2 + Math.random() * 4}px`, background: i % 3 === 0 ? '#ff6b00' : i % 3 === 1 ? '#fbbf24' : '#ef4444', left: `${10 + Math.random() * 80}%`, bottom: '20%', filter: 'blur(0.5px)' }}
          animate={{ y: [0, -200 - Math.random() * 200], opacity: [0, 0.8, 0], x: (Math.random() - 0.5) * 60 }}
          transition={{ duration: 3 + Math.random() * 3, delay: i * 0.4, repeat: Infinity }}
        />
      ))}

      <div className="max-w-4xl mx-auto relative z-10">
        <Reveal>
          <div className="text-center">
            {/* Label */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.12)' }}>
              <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Flame className="w-3.5 h-3.5" style={{ color: '#ff6b00' }} />
              </motion.div>
              <span className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: '#ff6b00' }}>Live Burn Tracker</span>
            </div>

            {/* The Big Number */}
            <div className="mb-2">
              <motion.p
                className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tight"
                style={{ fontFamily: "'Bebas Neue', cursive", background: 'linear-gradient(180deg, #ffffff 0%, #fbbf24 40%, #ff6b00 70%, #ef4444 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 30px rgba(255,107,0,0.3))' }}
                data-testid="burn-counter-number"
              >
                {displayBurned > 0 ? formatNumber(displayBurned) : '0'}
              </motion.p>
              <p className="text-xs sm:text-sm font-mono tracking-[0.3em] uppercase mt-1" style={{ color: 'rgba(255,107,0,0.5)' }}>
                $GFG Burned Forever
              </p>
            </div>

            {/* Supply bar */}
            <div className="max-w-md mx-auto mt-8 mb-8">
              {(() => {
                const rawPct = Number(stats?.supply_reduction_pct);
                const safePct = Number.isFinite(rawPct) ? Math.max(0, Math.min(100, rawPct)) : 0;
                const remaining = (100 - safePct).toFixed(2);
                return (
                  <>
                    <div className="flex justify-between text-[10px] font-mono mb-1.5">
                      <span className="text-white/20">Remaining Supply</span>
                      <span style={{ color: '#ff6b00' }}>{remaining}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <motion.div
                        className="h-full rounded-full"
                        initial={{ width: '100%' }}
                        animate={{ width: `${remaining}%` }}
                        transition={{ duration: 2, ease: 'easeOut' }}
                        style={{ background: 'linear-gradient(90deg, #22c55e, #fbbf24, #ff6b00)' }}
                      />
                    </div>
                  </>
                );
              })()}
              <div className="flex justify-between text-[9px] font-mono mt-1">
                <span className="text-white/15">0</span>
                <span className="text-white/15">1,000,000,000</span>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {[
                { label: 'Burn Transactions', value: stats?.total_burn_txns || 0, color: '#ff6b00' },
                { label: 'Unique Burners', value: stats?.total_burners || 0, color: '#fbbf24' },
                { label: 'Active Players', value: stats?.total_players || 0, color: '#a855f7' },
                { label: 'Seasons Completed', value: stats?.completed_seasons || 0, color: '#22c55e' },
              ].map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 + i * 0.1 }}
                  className="p-3 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <p className="text-lg md:text-xl font-black" style={{ color: s.color }}>{s.value.toLocaleString()}</p>
                  <p className="text-[9px] font-mono text-white/25 tracking-wider uppercase mt-0.5">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═══ GUIDED ONBOARDING — First-Time Experience ═══
const OnboardingOverlay = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const steps = [
    {
      icon: Wallet,
      title: "Welcome to the Sewers",
      desc: "Gas-Free Gremlins is a Solana meme token with a free-to-play arcade. Connect your wallet to get started or jump in as a guest.",
      cta: "Next",
      color: '#a855f7',
    },
    {
      icon: Gamepad2,
      title: "Play Free Games",
      desc: "Every arcade game runs on in-game credits with zero monetary value. Try Safe Cracker, Snatch-a-Bag, Fee Scrambler, or Price Oracle to stack Season Points.",
      cta: "Next",
      color: '#22c55e',
    },
    {
      icon: Flame,
      title: "Burn $GFG, Earn More",
      desc: "Holders can voluntarily burn $GFG through the Burn Portal for bonus Season Points. Every burn permanently reduces supply. More burns means a bigger share of community rewards.",
      cta: "Next",
      color: '#ff6b00',
    },
    {
      icon: Trophy,
      title: "Compete in Seasons",
      desc: "A new Season kicks off every 30 days with community rewards up for grabs. Earn Season Points through gameplay and burns. Hit the community burn target and rewards distribute to all participants.",
      cta: "Enter the Sewers",
      color: '#fbbf24',
    },
  ];

  const s = steps[step];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }}
      data-testid="onboarding-overlay"
    >
      {/* Ambient glow */}
      <motion.div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full pointer-events-none"
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 4, repeat: Infinity }}
        style={{ background: `radial-gradient(circle, ${s.color}20, transparent 70%)` }} />

      <motion.div key={step} initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-md w-full text-center">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((_, i) => (
            <div key={i} className="h-1 rounded-full transition-all duration-500"
              style={{ width: i === step ? '32px' : '8px', background: i === step ? s.color : 'rgba(255,255,255,0.1)' }} />
          ))}
        </div>

        {/* Icon */}
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
          style={{ background: `${s.color}15`, border: `1px solid ${s.color}30`, boxShadow: `0 0 40px ${s.color}15` }}>
          <s.icon className="w-8 h-8" style={{ color: s.color }} />
        </motion.div>

        {/* Content */}
        <h2 className="text-2xl sm:text-3xl font-black text-white mb-3" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.02em' }}>
          {s.title}
        </h2>
        <p className="text-sm text-white/40 leading-relaxed mb-10 px-4">
          {s.desc}
        </p>

        {/* CTA */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => {
            if (step < steps.length - 1) {
              setStep(step + 1);
            } else {
              localStorage.setItem('gfg_onboarded', 'true');
              onComplete();
            }
          }}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-bold transition-all"
          style={{ background: `${s.color}15`, border: `1px solid ${s.color}30`, color: s.color }}
          data-testid="onboarding-cta"
        >
          {s.cta}
          <ArrowRight className="w-4 h-4" />
        </motion.button>

        {/* Skip */}
        <button
          onClick={() => { localStorage.setItem('gfg_onboarded', 'true'); onComplete(); }}
          className="block mx-auto mt-4 text-[10px] font-mono text-white/15 hover:text-white/30 transition-colors tracking-wider"
          data-testid="onboarding-skip"
        >
          SKIP INTRO
        </button>
      </motion.div>
    </motion.div>
  );
};



/* ─── Features — "What Awaits You" (original gremlin-image grid) ──
   Six lore-themed obsidian cards, each with a hand-drawn gremlin
   image, gold corner brackets, and a colored accent on hover. */
const Features = () => {
  const navigate = useNavigate();
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const features = [
    {
      numeral: 'I',
      icon: Gamepad2,
      title: "The Fee Heist Arcade",
      tagline: "Steal back what the chain ate.",
      desc: "Free-to-play mini-games that earn Season Points every round. SP becomes your rank in the monthly burn cycle, your airdrop tier, your name in the Hall of Fame. The grind compounds.",
      utility: "Earn SP · No wallet needed · Free entry",
      image: "https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/q2q8nn7r_Gremlin%202.png",
      link: "/arcade",
      accent: '#ff8c00',
      accentRGB: '255,140,0',
      cta: 'Enter the Heist',
      featured: true,
    },
    {
      numeral: 'II',
      icon: Skull,
      title: "The Descent",
      tagline: "Ten floors. The deeper, the worthier.",
      desc: "Ten cinematic floors of escalating risk. Every floor pays out heavy SP and recovered fees, and the bottom floors pay the worthiest rewards. Climb high enough and your name carries weight when the next season opens.",
      utility: "Heavy SP · Fee recovery · 10 floors",
      image: "https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/1ala7eao_Gremlin%204.png",
      link: "/arcade?game=the-descent",
      accent: '#c026d3',
      accentRGB: '192,38,211',
      cta: 'Begin the Descent',
      featured: true,
    },
    {
      numeral: 'III',
      icon: Flame,
      title: "Seasons & Burn Portal",
      tagline: "Every thirty days, the supply tightens.",
      desc: "$GFG holders burn tokens for SP during each season. Hit the community target and the protocol releases rewards back to the most committed. The token gets scarcer, the holders get richer, the cycle deepens.",
      utility: "Burn for SP · Community rewards · Permanent burns",
      image: "https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/qqksq8x2_Gremlin-1.png",
      link: "/seasons",
      accent: '#dc2626',
      accentRGB: '220,38,38',
      cta: 'Open the Portal',
    },
    {
      numeral: 'IV',
      icon: BarChart3,
      title: "The Public Ledger",
      tagline: "Nothing is hidden. The chain remembers.",
      desc: "Live $GFG price, market cap, holder count, burn velocity, and watched-souls metrics. Pulled directly from Solana — no curated dashboards, no spin. The community sees everything the team sees.",
      utility: "Real-time chain data · Transparent metrics · No spin",
      image: "https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/oyefid8i_gremlin%203.png",
      link: "/stats",
      accent: '#eab308',
      accentRGB: '234,179,8',
      cta: 'Read the Ledger',
    },
    {
      numeral: 'V',
      icon: Coins,
      title: "The Airdrop Tithe",
      tagline: "The earliest souls receive the largest gifts.",
      desc: "Quests, oaths, and arcade rounds earn Airdrop Points. No deposit, no minimums, no purchase required. When the token mints, the souls who arrived first claim the heaviest tithes from the protocol's vault.",
      utility: "Free $GFG · Quests + oaths · No purchase needed",
      image: "https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/8rah0x3r_gremlin%207.png",
      link: "/airdrop",
      accent: '#14b8a6',
      accentRGB: '20,184,166',
      cta: 'Claim Your Tithe',
    },
    {
      numeral: 'VI',
      icon: Skull,
      title: "The Graveyard",
      tagline: "Even your worst trades can pay tribute.",
      desc: "Bury a failed Solana transaction in the Graveyard. Recover 50,000 lamports plus 25 SP per verified TX. The chain never forgets your mistakes — but here, it can finally pay you back for them.",
      utility: "50K lamports recovered · 25 SP per TX · Verified on-chain",
      image: "https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/nym4u9u9_Gremlin%205.png",
      link: "/graveyard",
      accent: '#6366f1',
      accentRGB: '99,102,241',
      cta: 'Bury a Soul',
    },
    {
      numeral: 'VII',
      icon: Crown,
      title: "The Hall of Ghosts",
      tagline: "Every season, four souls are bound forever.",
      desc: "When a season closes, the four highest souls are written on-chain by the Sovereign — wallet, rank, and final SP carved into the Codex permanently. Their ghosts drift forever through the hall. Not a leaderboard. A graveyard for the worthy.",
      utility: "On-chain immortality · 4 ghosts per season · Permanent record",
      image: "https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/8rah0x3r_gremlin%207.png",
      link: "/ghosts",
      accent: '#ffd86b',
      accentRGB: '255,216,107',
      cta: 'Walk the Hall',
    },
  ];

  return (
    <section className="py-16 md:py-32 px-4 sm:px-6 relative overflow-x-clip" data-testid="what-awaits-section">
      {/* Section ambient glow based on hovered card */}
      <div className="absolute inset-0 transition-opacity duration-700 pointer-events-none"
           style={{
             background: hoveredIdx !== null
               ? `radial-gradient(ellipse at 50% 50%, rgba(${features[hoveredIdx].accentRGB},0.10) 0%, transparent 55%)`
               : 'transparent',
           }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white text-center mb-3 md:mb-4">
            What Awaits <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">You</span>
          </h2>
          <p className="text-center text-white/30 text-sm md:text-lg mb-12 md:mb-16 max-w-lg mx-auto px-2">Choose your path through the sewers</p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((f, i) => (
            <Reveal key={f.title} direction="up" delay={i * 0.10}>
              <FeatureCardLore
                feature={f}
                index={i}
                hovered={hoveredIdx === i}
                onHoverStart={() => setHoveredIdx(i)}
                onHoverEnd={() => setHoveredIdx(null)}
                onClick={() => {
                  if (f.link === '/arcade?game=the-descent' && isMobile) {
                    navigate('/arcade');
                  } else {
                    navigate(f.link);
                  }
                }}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Single lore-themed feature card — obsidian + gold corner brackets, accent glow on hover */
const FeatureCardLore = ({ feature: f, hovered, onHoverStart, onHoverEnd, onClick }) => {
  const accentSoft = `rgba(${f.accentRGB},0.55)`;
  const accentDim = `rgba(${f.accentRGB},0.18)`;
  const isFeatured = f.featured;

  // Featured cards always show the "active" border/shadow style
  const showActive = hovered || isFeatured;

  return (
    <motion.div
      onClick={onClick}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      className="relative cursor-pointer group h-[460px] flex flex-col"
      data-testid={`what-awaits-card-${f.numeral.toLowerCase()}`}
      style={{
        background:
          'linear-gradient(180deg, rgba(20,8,12,0.78) 0%, rgba(8,4,8,0.92) 100%)',
        border: `1px solid ${showActive ? accentSoft : 'rgba(220,200,120,0.16)'}`,
        borderRadius: 4,
        boxShadow: showActive
          ? `0 0 32px rgba(${f.accentRGB},0.28), 0 0 64px rgba(${f.accentRGB},0.14), inset 0 0 28px rgba(0,0,0,0.55)`
          : '0 0 18px rgba(0,0,0,0.45), inset 0 0 22px rgba(0,0,0,0.45)',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        overflow: 'hidden',
      }}
    >
      {/* FEATURED outer pulsing aura — only on featured cards */}
      {isFeatured && (
        <motion.div
          aria-hidden
          className="absolute -inset-[2px] pointer-events-none rounded-[6px]"
          style={{
            background: `linear-gradient(135deg, rgba(${f.accentRGB},0.55) 0%, rgba(${f.accentRGB},0) 35%, rgba(${f.accentRGB},0) 65%, rgba(${f.accentRGB},0.55) 100%)`,
            filter: 'blur(6px)',
            zIndex: 0,
          }}
          animate={{ opacity: [0.45, 0.85, 0.45] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* FEATURED ribbon badge — top left, above the numeral */}
      {isFeatured && (
        <motion.div
          aria-hidden
          className="absolute top-3 left-5 z-40 pointer-events-none"
          animate={{ y: [0, -1.5, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div
            className="font-mono uppercase inline-flex items-center gap-1.5 px-2.5 py-1"
            style={{
              fontSize: '0.55rem',
              letterSpacing: '0.32em',
              color: f.accent,
              background: `linear-gradient(180deg, rgba(${f.accentRGB},0.16), rgba(${f.accentRGB},0.06))`,
              border: `1px solid rgba(${f.accentRGB},0.55)`,
              borderRadius: 2,
              boxShadow: `0 0 14px rgba(${f.accentRGB},0.45)`,
              textShadow: `0 0 6px rgba(${f.accentRGB},0.7)`,
            }}
          >
            <Sparkles className="w-2.5 h-2.5" style={{ color: f.accent }} />
            Featured
          </div>
        </motion.div>
      )}

      {/* Gold corner brackets */}
      <div className="absolute top-2 left-2 w-3.5 h-3.5 border-t border-l z-30"
        style={{ borderColor: showActive ? accentSoft : 'rgba(220,200,120,0.45)' }} />
      <div className="absolute top-2 right-2 w-3.5 h-3.5 border-t border-r z-30"
        style={{ borderColor: showActive ? accentSoft : 'rgba(220,200,120,0.45)' }} />
      <div className="absolute bottom-2 left-2 w-3.5 h-3.5 border-b border-l z-30"
        style={{ borderColor: showActive ? accentSoft : 'rgba(220,200,120,0.45)' }} />
      <div className="absolute bottom-2 right-2 w-3.5 h-3.5 border-b border-r z-30"
        style={{ borderColor: showActive ? accentSoft : 'rgba(220,200,120,0.45)' }} />

      {/* Subtle radial accent — stronger on featured */}
      <div className="absolute pointer-events-none inset-0"
        style={{
          background: `radial-gradient(ellipse at 20% 100%, rgba(${f.accentRGB},${isFeatured ? 0.18 : 0.10}), transparent 55%)`,
        }} />

      {/* Header strip — Roman numeral + icon */}
      <div className={`relative z-10 flex items-center justify-between px-5 ${isFeatured ? 'pt-9' : 'pt-5'}`}>
        <div className="flex items-center gap-3">
          <span
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '1.05rem',
              fontWeight: 700,
              color: 'rgba(220,200,120,0.6)',
              letterSpacing: '0.08em',
            }}
          >
            {f.numeral}.
          </span>
          <f.icon className="w-4 h-4" style={{ color: f.accent, opacity: showActive ? 1 : 0.75, transition: 'opacity 0.3s' }} />
        </div>
        <span
          className="font-mono uppercase"
          style={{
            fontSize: '0.56rem',
            letterSpacing: '0.32em',
            color: showActive ? f.accent : 'rgba(220,200,120,0.42)',
            transition: 'color 0.3s',
          }}
        >
          {f.utility.split(' · ')[0]}
        </span>
      </div>

      {/* Image — centered */}
      <div className="relative h-44 md:h-48 flex items-center justify-center mt-2 mb-2">
        {/* Ambient glow behind — featured cards always animate */}
        <motion.div
          className="absolute pointer-events-none"
          style={{
            width: 180, height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(${f.accentRGB},${isFeatured ? 0.30 : 0.20}) 0%, transparent 70%)`,
            filter: 'blur(24px)',
          }}
          animate={(hovered || isFeatured) ? { scale: [0.95, 1.12, 0.95], opacity: [0.5, 0.9, 0.5] } : { scale: 1, opacity: 0.35 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.img
          src={f.image}
          alt={f.title}
          loading="lazy"
          decoding="async"
          className="relative z-10 w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 object-contain"
          draggable={false}
          animate={(hovered || isFeatured) ? { y: [0, -5, 0], scale: hovered ? 1.06 : 1.02 } : { y: 0, scale: 1 }}
          transition={(hovered || isFeatured)
            ? { y: { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }, scale: { duration: 0.4 } }
            : { duration: 0.4 }
          }
          style={{ filter: (hovered || isFeatured) ? `drop-shadow(0 0 14px rgba(${f.accentRGB},0.5))` : 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }}
        />
      </div>

      {/* Divider rune */}
      <div className="relative z-10 px-5">
        <div
          className="w-full"
          style={{
            height: 1,
            background: `linear-gradient(90deg, transparent, ${accentDim}, transparent)`,
          }}
        />
      </div>

      {/* Body */}
      <div className="relative z-10 px-5 pt-3 pb-5 flex-1 flex flex-col">
        <h3
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: '1.18rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: '#e6dcc8',
            textShadow: showActive ? `0 0 12px rgba(${f.accentRGB},0.4)` : 'none',
            transition: 'text-shadow 0.3s',
            marginBottom: 4,
          }}
        >
          {f.title}
        </h3>
        <p
          className="italic"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: '0.92rem',
            color: 'rgba(230,220,200,0.62)',
            lineHeight: 1.5,
            marginBottom: 8,
          }}
        >
          {f.tagline}
        </p>
        <p
          style={{
            fontSize: '0.78rem',
            color: 'rgba(230,220,200,0.45)',
            lineHeight: 1.55,
            flex: 1,
          }}
        >
          {f.desc}
        </p>

        {/* Spacer + divider above CTA */}
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${accentDim}` }} />

        {/* CTA strip — bright + readable always, text centered */}
        <div
          className="relative flex items-center justify-center rounded-sm px-3 py-2.5"
          style={{
            background: hovered
              ? `linear-gradient(90deg, rgba(${f.accentRGB},0.18), rgba(${f.accentRGB},0.04))`
              : `linear-gradient(90deg, rgba(${f.accentRGB},0.10), rgba(${f.accentRGB},0.02))`,
            transition: 'background 0.3s',
          }}
        >
          <span
            className="font-mono uppercase font-bold leading-none text-center"
            style={{
              fontSize: '0.7rem',
              letterSpacing: '0.20em',
              color: f.accent,
              textShadow: `0 0 10px rgba(${f.accentRGB},0.7), 0 1px 2px rgba(0,0,0,0.9)`,
              transition: 'color 0.3s, text-shadow 0.3s',
            }}
          >
            {f.cta}
          </span>
          <motion.span
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center"
            style={{
              color: f.accent,
              filter: `drop-shadow(0 0 6px rgba(${f.accentRGB},0.7))`,
              transition: 'color 0.3s',
            }}
            animate={(hovered || isFeatured) ? { x: [0, 4, 0] } : { x: 0 }}
            transition={{ duration: 1.1, repeat: Infinity }}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
};



export default function HomePage() {
  const { isDemo } = useDemo();
  // PREVIEW_MODE — the PreviewGate splash replaces the OnboardingOverlay entirely.
  const [showOnboarding, setShowOnboarding] = useState(() => {
    if (PREVIEW_MODE) return false;
    if (sessionStorage.getItem('gfg_demo') === 'true') return false;
    return localStorage.getItem('gfg_onboarded') !== 'true';
  });

  return (
    <div className="bg-black text-white min-h-screen relative">
      <AnimatePresence>
        {showOnboarding && <OnboardingOverlay onComplete={() => setShowOnboarding(false)} />}
      </AnimatePresence>
      <FloatingParticles />
      <Hero />
      <BurnCounter />
      <ProjectExplainer />
      <Features />
      <SeasonWidget />
      <GenesisShrine />
      <CTA />
      <HomeFooter />
    </div>
  );
}
